import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle,
  useMemo, useRef, useState, ChangeEvent, FormEvent,
} from 'react';
import { collection, doc, getDoc, getDocs, onSnapshot, query } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { auth, db } from '../../../../firebase';
import { authFetch } from '../../../utils/cloudFunctionsAuth';
import { PanelEmailData, useEmailPanel } from '../../mail/contexts/EmailPanelContext';
import { useAppData } from '../../../providers/AppDataProvider';
import { useIsNarrow } from '../../../hooks/useIsNarrow';
import { warmSendSound, playSendSound } from '../../../utils/sendSound';
import { getCachedGraphEmailBody, getOrFetchGraphEmailBody } from '../../mail/utils/graphEmailBodyCache';
import { fetchGraphAttachmentsList, resolveInlineGraphImages } from '../../mail/utils/inlineImageResolver';
import { replyRecipients, type ReplyRecipients } from '../../mail/utils/replyRecipients';
import { DEFAULT_FEED_MAILBOXES } from '../../mail/utils/feedWindow';
import {
  ComposerAttachment,
  FORWARD_IMAGE_MAX_BYTES,
  MAIL_ATTACH_MAX_FILE_BYTES,
  MAIL_ATTACH_MAX_TOTAL_BYTES,
  attachmentBlockReason,
  attachmentNameSignature,
  attachmentStateSignature,
  base64DecodedBytes,
  checkCanAdd,
  classify,
  formatBytes,
  formatLimit,
  formatTotalIndicator,
  hydrateAttachments,
  newAttachmentId,
  removeCidImages,
  resolveForwardCidImages,
  sumAttachmentBytes,
  sumInlineBytes,
  toPersistedAttachments,
  toSendAttachments,
} from '../../mail/utils/attachmentLimits';
import { deleteAttachments, describeUploadError, uploadAttachment, uploadInlineImage } from '../../mail/utils/attachmentStorage';
import { dataImageCount, inlineImagePaths, storageImageBytes } from '../../mail/utils/inlineImages';
import { DRAFT_TOO_LARGE_CODE } from '../../../services/draftService';
import SnippetPicker, { SnippetPickerCloseReason } from '../../mail/components/SnippetPicker';
import {
  SNIPPET_VARIABLES, buildSnippetContext, countUnresolvedVariables, describeUnfilledVariables,
  findUnresolvedVariables, personName, unfilledTokenText,
} from '../../mail/utils/snippetVars';
import TerminalRichText, { TerminalRichTextHandle } from './TerminalRichText';
import TerminalChipInput, { EmailOption } from './TerminalChipInput';
import './TerminalSendEmailForm.scss';

/**
 * TerminalSendEmailForm — purpose-built terminal-theme composer. Replaces
 * SendEmailForm *only* for the terminal theme code path; the classic
 * SendEmailForm is untouched and still used by the non-terminal modal.
 *
 * Scope-preserved features (same as classic):
 *   - Multiple sender accounts (hidden when only one exists)
 *   - To / Cc / Bcc with creatable emails + client-list autocomplete
 *     ranked by host priority
 *   - Subject
 *   - Rich-text body via Tiptap (TerminalRichText) — no CKEditor here
 *   - Insert signature at cursor
 *   - Reply snippets (shared library, Ctrl/Cmd+J): {{variables}} resolved on
 *     insert; Send / Send-later refuse while one is left unfilled in the
 *     owner's own text (see SnippetPicker + mail/utils/snippetVars)
 *   - File attachments (20 MB per file / 20 MB total; small files stay inline
 *     base64, bigger ones upload to Cloud Storage and only a ref is kept in
 *     drafts + the send payload — see mail/utils/attachmentLimits.ts)
 *   - Forward keeps the original's attachments ("from original" chips)
 *   - Quoted thread is merged directly into the editor body on reply/
 *     forward (Gmail-style) — user can edit or delete it inline
 *   - Auto-save every 3s (debounced) + every 30s (interval) + on unmount
 *   - Manual draft save via ref.triggerSave()
 *   - Draft load via EmailPanelContext (setDraftMode('edit', draft))
 *   - Reply / reply-all / forward initialData prefill
 *   - Status exposure via ref (hasUnsavedChanges, currentDraftId)
 *
 * UI differences from classic:
 *   - All chrome bracketed-text mono, no Bootstrap pills
 *   - From dropdown hidden when exactly one account configured
 *   - Auto-save status hidden unless save failed (per the pitch spec)
 *   - Link UX is an inline popover (inside TerminalRichText)
 *   - Errors render as a dismissible log-line with [retry], not a bar
 *   - Quoted thread collapsed by default to one line
 */

// ── Helpers (copied from SendEmailForm — stable; do not diverge) ────────

const formatReplySubject = (orig?: string): string => {
  if (!orig) return 'Re: ';
  return orig.toLowerCase().startsWith('re:') ? orig : `Re: ${orig}`;
};
const formatForwardSubject = (orig?: string): string => {
  if (!orig) return 'Fwd: ';
  return orig.toLowerCase().startsWith('fwd:') ? orig : `Fwd: ${orig}`;
};

// Defensive HTML cleaner used when building quoted-reply blocks. Copied
// verbatim-in-spirit from classic — drops empty wrappers that make
// quoted content balloon visually.
//
// cid: images: a quoted <img src="cid:..."> points at an attachment of the
// ORIGINAL message and can never resolve in a new one — it renders as a
// broken-image box for the recipient and in the editor. Replies drop them
// (default); a forward passes keepCid so it can resolve the small ones to
// data: URLs first (resolveForwardCidImages) and strips whatever is left.
const cleanEmailHtmlForReply = (html: string, opts?: { keepCid?: boolean }): string => {
  if (!html) return '';
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    // Remove <style> blocks so they don't leak to the new compose
    doc.querySelectorAll('style, script, meta, link, title, base').forEach(n => n.remove());
    const body = doc.body;
    const out = body ? body.innerHTML : html;
    return opts?.keepCid ? out.trim() : removeCidImages(out).trim();
  } catch {
    return opts?.keepCid ? html : removeCidImages(html);
  }
};

// Escape + convert PLAIN TEXT to quotable HTML. Splicing raw text into the
// editor's HTML collapses every newline (the "forwarded email turns into one
// run-on paragraph" bug) — and unescaped text is a markup-injection hole.
const escapeHtml = (s: string): string =>
  String(s || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
const textToQuotedHtml = (t: string): string =>
  `<div>${escapeHtml(String(t || '').trim()).replace(/\r?\n/g, '<br>')}</div>`;

// Strip Re:/Fwd:/Fw: prefixes for the quoted-header "Subject:" line — the
// panel hands us a subject that may already be prefixed for the composer's
// subject FIELD, but the quote header must show the ORIGINAL subject.
const stripSubjectPrefixes = (s: string): string =>
  String(s || '').replace(/^\s*((re|fwd?|fw):\s*)+/i, '').trim();

/**
 * Resolve the BEST quotable body for a reply/forward. The feed/reader email
 * object usually carries only a short plain-text snippet (`text` is Graph's
 * truncated bodyPreview; full bodies live in the email_bodies store) — quoting
 * that loses all formatting AND cuts off mid-sentence. Priority:
 *   1. initialData.html when it's a real body (not a stub),
 *   2. the body cache (synchronously warm in practice — the reader fetched it
 *      to display the email you're replying to),
 *   3. the text snippet, escaped + <br>-converted, flagged so the caller can
 *      upgrade it asynchronously when the cache was cold.
 */
function resolveQuotedSource(initialData: PanelEmailData): { html: string; usedTextFallback: boolean; emailId: string } {
  const emailId = String((initialData as any)?.emailId || initialData?.id || '');
  const ownHtml = String(initialData?.html || '');
  if (ownHtml.length > 200) return { html: ownHtml, usedTextFallback: false, emailId };
  if (emailId) {
    const cached = getCachedGraphEmailBody(emailId, 'html');
    if (cached?.content) {
      return cached.contentType === 'html'
        ? { html: cached.content, usedTextFallback: false, emailId }
        : { html: textToQuotedHtml(cached.content), usedTextFallback: false, emailId };
    }
  }
  if (ownHtml) return { html: ownHtml, usedTextFallback: false, emailId };
  return { html: textToQuotedHtml(initialData?.text || ''), usedTextFallback: true, emailId };
}

const getFileAsBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

function emailAddress(addr: string): string {
  // "John Smith <john@x.com>" → "john@x.com"; passes plain emails through.
  const m = /<([^>]+)>/.exec(addr);
  return m ? m[1].trim() : addr.trim();
}

// ── Pure helpers (exported so they can be unit-tested) ──────────────────

/**
 * Decoded bytes of every pasted `data:image/...;base64,` picture in the body.
 * executeGraphSend turns each one into an inline (cid:) attachment and counts
 * it toward the 20 MiB total (same <img src="data:..."> pattern), so the
 * composer has to count it too or its "x MB of 20 MB" indicator and pick-time
 * check under-report and the 413 only arrives after the undo countdown.
 */
export function inlineImageBytes(html: string): number {
  if (!html || html.indexOf('data:image/') < 0) return 0;
  const re = /<img\b[^>]*?\bsrc\s*=\s*["']data:image\/[a-zA-Z0-9.+-]+;base64,([^"']+)["']/gi;
  let total = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) total += base64DecodedBytes(m[1]);
  return total;
}

/**
 * The part of the body the OWNER wrote: everything before the first `<hr>` (the
 * quoted-thread anchor) with the auto-inserted signature taken out. The quote
 * and the signature arrive without a keystroke, so neither may count as the
 * owner's words (ghost drafts, "says attached" guard).
 */
export function ownerRegionHtml(html: string, signatureHtml: string): string {
  const hrIdx = html.indexOf('<hr>');
  let owner = hrIdx >= 0 ? html.slice(0, hrIdx) : html;
  if (signatureHtml) owner = owner.replace(signatureHtml, '');
  return owner;
}

/**
 * placeholder -> how often it occurs, under the names findUnresolvedVariables reports.
 * Build one with emptyVariableCounts() / variableCounts(), never `{}`: a placeholder
 * can be named `constructor` or `__proto__`, and a plain object answers for the first
 * with a function and swallows a write to the second.
 */
export type VariableCounts = Record<string, number>;

/** A counts map with no prototype (see VariableCounts). */
export const emptyVariableCounts = (): VariableCounts => Object.create(null);

/** One entry of a counts map, read as an OWN key only - safe even for a caller's plain `{}`. */
const countOf = (counts: VariableCounts, name: string): number =>
  (Object.prototype.hasOwnProperty.call(counts, name) ? counts[name] : 0) || 0;

/** One of the five snippet fields (firstName, customerName, ...) as opposed to any other placeholder. */
export const isSnippetVariable = (name: string): boolean => SNIPPET_VARIABLES.some((v) => v.name === name);

/**
 * How many of each placeholder `html` holds. snippetVars' countUnresolvedVariables is
 * the one scan behind this AND findUnresolvedVariables (what a placeholder IS: well-formed
 * or not, in text or in a link, comments dropped, an unclosed `{{` harmless), so the
 * counts and the send guard can never disagree. It returns a prototype-less map.
 */
export function variableCounts(html: string): VariableCounts {
  return countUnresolvedVariables(html);
}

/**
 * Every {{placeholder}} that is the OWNER's, for the send guard, the amber note and
 * the ghost-draft check. Above the first `<hr>` (minus the signature) every token is.
 * Below it is the quoted thread, which legitimately holds other people's tokens (a
 * client's broken mail-merge) - but the owner can put the caret down there too
 * (select-all then a snippet, a click under the quote, a typed `---` rule), so the
 * tokens below count as theirs once there are MORE of a name than the quote brought.
 * `quoteBaseline` is what the machine-built quote held (see quoteVarBaselineRef);
 * with none recorded, nothing below the rule is excused.
 */
export function unfilledVariables(html: string, signatureHtml: string, quoteBaseline: VariableCounts = emptyVariableCounts()): string[] {
  const found = findUnresolvedVariables(ownerRegionHtml(html, signatureHtml));
  const hrIdx = html.indexOf('<hr>');
  if (hrIdx < 0) return found;
  const below = variableCounts(html.slice(hrIdx));
  Object.keys(below).forEach((name) => {
    if (below[name] > countOf(quoteBaseline, name) && found.indexOf(name) < 0) found.push(name);
  });
  return found;
}

/** True when the owner's OWN words promise an attachment ("invoice attached"). */
export function mentionsAttachment(ownerHtml: string): boolean {
  const text = ownerHtml.replace(/<[^>]*>?/gm, ' ').toLowerCase();
  return /\b(attach(ed|ment|ing)?|enclosed|find (the |your )?(invoice|file|pdf|document|attachment)|please find|see attached)\b/.test(text);
}

const firstWord = (s: unknown): string => String(s ?? '').trim().split(/\s+/)[0] || '';

/**
 * {{myFirstName}} for the mailbox the mail goes out FROM. The profile name belongs
 * to whoever is signed in, which is not who signs a message sent from the other
 * person's mailbox ("Regards, Ben" above Lidia's signature). A mailbox local part
 * (`lidia@`) is taken as the name only when it is ONE alphabetic word that is not a
 * role word (personName refuses accounts / info / admin ...) AND the mailbox's own
 * signature spells it out: that is what tells `lidia@` from a shared `leads@` or
 * `webconstruct@`, which keep the signed-in user's name. Anything unconfirmed falls
 * back to the profile name (empty when that is unknown, so the gap stays visible).
 */
export function senderFirstName(
  account: { mailbox?: string; signatureHtml?: string } | undefined,
  profileFirstName: string,
): string {
  const profile = String(profileFirstName || '').trim();
  const local = String(account?.mailbox || '').split('@')[0].trim();
  const candidate = /^[A-Za-z]{2,}$/.test(local) ? personName(local)?.first : undefined;
  if (!candidate) return profile;
  if (candidate.toLowerCase() === profile.toLowerCase()) return profile; // the same person
  const signatureWords = String(account?.signatureHtml || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .toLowerCase()
    .split(/[^a-z\u00e0-\u00ff]+/);
  return signatureWords.indexOf(candidate.toLowerCase()) >= 0 ? candidate : profile;
}

const SEND_UNSURE_TAIL = 'the email may already have gone out. Check Sent Items before sending again.';
const SEND_CONNECTION_LOST = `connection lost while sending - ${SEND_UNSURE_TAIL}`;
const INVOICE_BUSY_REASON = 'generating the invoice PDF - send is available when it finishes';
const IMAGE_UPLOAD_BUSY_REASON = 'a pasted picture is still uploading - send is available when it finishes';

export type SendOutcome =
  | { kind: 'sent' }
  /** The server said, explicitly, that nothing went out. Safe to arm Send again. */
  | { kind: 'rejected'; message: string }
  /** No trustworthy answer: the email MAY be in Sent Items. Never offer a one-click resend. */
  | { kind: 'unknown'; message: string };

/**
 * What do we actually KNOW after the send endpoint answered? Sends are
 * long-running (540s function, big files go draft -> upload session -> send) and
 * the server has no idempotency key, so a failure at the very last step can
 * leave the mail already delivered. Only a well-formed JSON refusal with a 4xx
 * proves it was NOT sent; a 5xx, a gateway timeout page, an unreadable body all
 * leave it open. (413 comes from the platform in front of the function, before
 * any handler ran, so it is a safe refusal too.)
 */
export function classifySendResponse(status: number, data: any): SendOutcome {
  if (data?.ok) return { kind: 'sent' };
  const serverMsg = typeof data?.error === 'string' && data.error
    ? data.error
    : (typeof data?.message === 'string' ? data.message : '');
  if (data && data.ok === false && status >= 400 && status < 500) {
    return { kind: 'rejected', message: `send failed: ${serverMsg || 'unknown error'}` };
  }
  if (status === 413) {
    return { kind: 'rejected', message: 'send failed: the message is too large to send - remove large pasted images or attach them as files' };
  }
  if (!data || typeof data !== 'object') {
    return { kind: 'unknown', message: `the server's answer could not be read (HTTP ${status}) - ${SEND_UNSURE_TAIL}` };
  }
  return { kind: 'unknown', message: `send status unclear: ${serverMsg || 'no usable answer'} (HTTP ${status}) - ${SEND_UNSURE_TAIL}` };
}

/**
 * What the server says did NOT travel with a sent mail: the forward note and
 * warnings[], de-duplicated. `expectedOriginals` = how many "from original"
 * chips the owner was looking at; if the server reports none carried over and
 * said nothing, still tell them - the chips promised files.
 */
export function sendWarningsFrom(data: any, expectedOriginals: number): string[] {
  const out: string[] = [];
  const add = (t: unknown) => {
    const s = typeof t === 'string' ? t.trim() : '';
    if (s && !out.includes(s)) out.push(s);
  };
  add(data?.forwardAttachments?.note);
  if (Array.isArray(data?.warnings)) data.warnings.forEach(add);
  const mode = data?.forwardAttachments?.mode;
  if (out.length === 0 && expectedOriginals > 0 && (mode === 'none' || mode === 'skipped')) {
    out.push('Sent, but the original attachments could not be included. Re-attach any files the recipient needs.');
  }
  return out;
}

/**
 * The invoice a server-made draft (recurring auto-draft, "email invoice") is
 * bound to. Only while its PDF is still attached: pulling the PDF off must drop
 * the link, or the send would mark the invoice SENT with nothing attached.
 */
export function invoiceLinkFromDraft(
  linkedInvoiceId: unknown,
  attachments: Array<{ filename: string }>,
): { id: string; filename: string } | null {
  const id = String(linkedInvoiceId ?? '').trim();
  if (!id) return null;
  const pdfs = attachments.filter((a) => /\.pdf$/i.test(a.filename));
  const pdf = pdfs.find((a) => /^invoice[-_ ]/i.test(a.filename)) || pdfs[0];
  return pdf ? { id, filename: pdf.filename } : null;
}

const countCidImages = (html: string): number =>
  (String(html || '').match(/<img\b[^>]*\bsrc\s*=\s*["']\s*cid:/gi) || []).length;
const countDataImages = (html: string): number =>
  (String(html || '').match(/<img\b[^>]*\bsrc\s*=\s*["']\s*data:image\//gi) || []).length;

/**
 * Forward: how many of the original's cid: pictures did NOT make it into the
 * editor. resolveForwardCidImages embeds the small ones as data: URLs and strips
 * the rest without a word (over 8 images / 400 KB). `before` = the html handed
 * to it, `after` = what came back.
 */
export function droppedForwardImages(before: string, after: string): number {
  const cid = countCidImages(before);
  if (cid === 0) return 0;
  const embedded = Math.max(0, countDataImages(after) - countDataImages(before));
  return Math.max(0, cid - embedded);
}

// ── Types ───────────────────────────────────────────────────────────────

interface SenderAccount {
  id: string;
  mailbox: string;
  displayName: string;
  signatureHtml: string;
  isEnabled: boolean;
}

export interface TerminalSendEmailFormProps {
  initialData?: PanelEmailData | null;
  /** The saved draft this composer was opened FOR (a workspace pane resumed / promoted from a
   *  draft): it alone may hydrate over a reply / forward initialData. */
  resumeDraftId?: string | null;
  onSendSuccess?: () => void;
  /** Emails the host wants to prioritize in autocomplete (job's customer
   *  + their sub-contacts). Deduplicated against the global client list. */
  priorityEmails?: EmailOption[];
}

export interface TerminalSendEmailFormRef {
  checkForUnsavedChanges: () => boolean;
  hasUnsavedChanges: boolean;
  currentDraftId: string | null;
  /** A file is still uploading - closing / expanding the composer now would drop it. */
  hasUploadInFlight: boolean;
  triggerSave: () => Promise<void>;
  prepareForDiscard: () => Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────

const SEND_URL = 'https://us-central1-web-construct-app.cloudfunctions.net/sendEmailGraphHttp';
const ACCOUNTS_URL = 'https://us-central1-web-construct-app.cloudfunctions.net/getGraphMailAccountsHttp';
const REWRITE_URL = 'https://us-central1-web-construct-app.cloudfunctions.net/rewriteEmailTextHttp';
const OUTBOX_URL  = 'https://us-central1-web-construct-app.cloudfunctions.net/manageEmailOutbox';

/** Send-later presets. Skips "this evening" once it's past ~5pm. */
function sendLaterPresets(): Array<{ label: string; ms: number }> {
  const out: Array<{ label: string; ms: number }> = [];
  const now = new Date();
  const evening = new Date(now); evening.setHours(18, 0, 0, 0);
  if (evening.getTime() > now.getTime() + 60 * 60 * 1000) {
    out.push({ label: 'This evening 6pm', ms: evening.getTime() });
  }
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1); tomorrow.setHours(8, 0, 0, 0);
  out.push({ label: 'Tomorrow 8am', ms: tomorrow.getTime() });
  const monday = new Date(now);
  const dow = monday.getDay(); // 0=Sun
  const add = ((8 - dow) % 7) || 7; // next Monday, always future
  monday.setDate(monday.getDate() + add); monday.setHours(8, 0, 0, 0);
  // On Sundays "tomorrow 8am" IS Monday 8am — don't offer the same time twice.
  if (monday.getTime() !== tomorrow.getTime()) {
    out.push({ label: 'Monday 8am', ms: monday.getTime() });
  }
  return out;
}
function fmtWhen(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })
    + ' ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}
const AUTOSAVE_DEBOUNCE_MS = 3000;
const AUTOSAVE_INTERVAL_MS = 30000;

// Module-level cache for the sender-accounts list so the composer doesn't
// re-fetch on every mount. The HTTP roundtrip can take seconds (cold
// start) and the result is what populates the "from" dropdown — so an
// uncached fetch on every open looks like a glacially-slow form.
// Cached value is reused for ACCOUNTS_TTL_MS, then a background refresh
// updates the cache for the next mount.
const ACCOUNTS_TTL_MS = 5 * 60 * 1000;
let cachedAccounts: SenderAccount[] | null = null;
let cachedAt = 0;
let inflightAccountsPromise: Promise<SenderAccount[]> | null = null;

// -- Who is "us" when a reply is pre-filled ----------------------------------
// Reply / reply-all address the OTHER side of the conversation. The rules
// (mail/utils/replyRecipients) take three separate answers, and blurring them is how a
// colleague gets silently dropped from Reply-all's Cc:
//   ownAddresses     "us" for FILTERING: never Cc'd, avoided in To. NARROW on purpose - the
//                    signed-in user and the account the mail goes out FROM (the email's own
//                    mailbox is added by the helper). Ben and Lidia are two people sharing the
//                    business: when a client wrote to both, Ben's reply-all keeps lidia@ on Cc.
//   selfAddresses    the signed-in person only: mail they authored is theirs wherever its copy
//                    sits (the copy that landed in Lidia's mailbox when Ben cc'd her).
//   accountAddresses every send account + feed mailbox (module cache as well as state - the
//                    state is still empty when the first prefill runs on a cold mount):
//                    authorship evidence ONLY for legacy docs that carry no mailbox. Never
//                    used for filtering.
function lowerAddresses(values: ReadonlyArray<unknown>): string[] {
  const out: string[] = [];
  values.forEach((v) => { if (typeof v === 'string' && v.trim()) out.push(v.trim().toLowerCase()); });
  return out;
}

function prefillReplyRecipients(
  data: PanelEmailData,
  fromMailbox: string | undefined,
  accounts: ReadonlyArray<{ mailbox?: string }>,
): ReplyRecipients {
  const me = auth?.currentUser?.email;
  return replyRecipients(data, {
    mode: data.isReplyAll ? 'replyAll' : 'reply',
    ownAddresses: lowerAddresses([me, fromMailbox]),
    selfAddresses: lowerAddresses([me]),
    accountAddresses: lowerAddresses([
      ...accounts.map((a) => a.mailbox),
      ...(cachedAccounts || []).map((a) => a.mailbox),
      ...DEFAULT_FEED_MAILBOXES,
    ]),
  });
}

const sameAddressList = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((x, i) => x === b[i]);

const TerminalSendEmailForm = forwardRef<TerminalSendEmailFormRef, TerminalSendEmailFormProps>(
  ({ initialData, resumeDraftId, onSendSuccess, priorityEmails = [] }, ref) => {

  // ── Account + signature state ─────────────────────────────────────────
  const [senderAccounts, setSenderAccounts] = useState<SenderAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [signatureHtml, setSignatureHtml] = useState<string>('');
  const [isFetchingAccounts, setIsFetchingAccounts] = useState(true);
  // User's preferred default reply account, persisted in users/{uid}.
  // When this is set AND we're starting a reply/forward, we pick that
  // account over the alphabetical first.
  const [defaultReplyAccountId, setDefaultReplyAccountId] = useState<string | null>(null);
  // Mirror of senderAccounts for effects that must read the list as it is NOW, not as it
  // was when the effect's closure was made (the reply prefill below).
  const senderAccountsRef = useRef<SenderAccount[]>([]);
  senderAccountsRef.current = senderAccounts;
  const selectedAccountIdRef = useRef<string>('');
  selectedAccountIdRef.current = selectedAccountId;
  /** Mailbox of the account currently selected in From (undefined until the list has landed). */
  const selectedFromMailbox = (): string | undefined =>
    senderAccountsRef.current.find((a) => a.id === selectedAccountIdRef.current)?.mailbox || undefined;

  // ── Recipients / subject / body ───────────────────────────────────────
  const [to,  setTo]  = useState<string[]>([]);
  const [cc,  setCc]  = useState<string[]>([]);
  const [bcc, setBcc] = useState<string[]>([]);
  const [subject, setSubject] = useState<string>('');
  const [htmlBody, setHtmlBody] = useState<string>('');
  const htmlBodyRef = useRef<string>('');

  // CC/BCC visibility: collapsed by default; auto-expand when prefilled
  const [showCcBcc, setShowCcBcc] = useState<boolean>(false);
  useEffect(() => {
    if (cc.length > 0 || bcc.length > 0) setShowCcBcc(true);
  }, [cc.length, bcc.length]);

  // ── Attachments ───────────────────────────────────────────────────────
  // Local items carry status/progress; only the persisted subset (contract B:
  // small inline base64 OR a Storage ref) is ever autosaved or sent. Every
  // write goes through commitAttachments so attachmentsRef — read by async
  // upload callbacks and the pick-time size check — never lags the state.
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([]);
  const attachmentsRef = useRef<ComposerAttachment[]>([]);
  const commitAttachments = useCallback((updater: (prev: ComposerAttachment[]) => ComposerAttachment[]) => {
    const next = updater(attachmentsRef.current);
    attachmentsRef.current = next;
    setAttachments(next);
  }, []);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Storage bookkeeping for the big files.
  const draftKeyRef = useRef<string>(newAttachmentId());          // path segment; rotates per composition
  const uploadAbortsRef = useRef<Map<string, AbortController>>(new Map());
  const uploadFilesRef = useRef<Map<string, File>>(new Map());    // held until uploaded so [retry] works
  const uploadedPathsRef = useRef<Set<string>>(new Set());        // objects THIS composer uploaded
  // Pictures pasted into the body live in Storage (the body holds only an <img src=url>), so a draft stays far
  // under Firestore's 1 MiB cap. Remember each one's size (it counts toward the 20 MiB total), how many are
  // uploading (Send waits), and whether an offload of embedded pictures is already running.
  const inlineImageSizesRef = useRef<Map<string, number>>(new Map());
  const [inlineImageUploads, setInlineImageUploads] = useState(0);
  const offloadingImagesRef = useRef(false);
  const lastSavedAttachSigRef = useRef<string>('');               // persisted attachments as last autosaved
  // Bumped every time the composition's attachments are reset (sent / discarded /
  // another draft or email opened). Async work that outlives one composition -
  // the invoice PDF fetch - compares it so a late result can never join the next.
  const compositionRef = useRef<number>(0);
  const lastSavedForwardSigRef = useRef<string>('');              // forward exclusions as last autosaved

  // ── Forward: keep the original's attachments ──────────────────────────
  // The server copies them (createForward) or re-attaches them; the composer
  // only remembers WHICH message, lists the files as read-only chips, and
  // reports the ones the user removed.
  const forwardFromEmailIdRef = useRef<string>('');
  const forwardFromGraphIdRef = useRef<string>('');
  const forwardTokenRef = useRef<number>(0); // drops stale attachment-list responses
  // `file` = a real file attachment. Attached emails / calendar items / links are
  // listed too (Graph copies them on a same-mailbox forward) but a cross-mailbox
  // forward can only copy files, so those chips carry a "may not be included" tag.
  const [forwardOriginals, setForwardOriginals] = useState<Array<{ name: string; size: number | null; file: boolean }>>([]);
  const [forwardOriginalsStatus, setForwardOriginalsStatus] = useState<'none' | 'loading' | 'loaded' | 'unavailable'>('none');
  const [forwardExcluded, setForwardExcluded] = useState<string[]>([]);
  const keptForwardOriginals = forwardOriginals.filter((o) => !forwardExcluded.includes(o.name));
  // Original cid: pictures that were too big to embed in the editor (stripped).
  const [forwardDroppedImages, setForwardDroppedImages] = useState(0);
  // A resumed forward draft is still reading back which originals the owner had
  // left out - Send waits for it, or the file they removed would go out.
  const [forwardRestoring, setForwardRestoring] = useState(false);
  const forwardExcludeSig = useMemo(() => [...forwardExcluded].sort().join('\u0000'), [forwardExcluded]);

  const resetForwardState = useCallback(() => {
    forwardTokenRef.current += 1;
    forwardFromEmailIdRef.current = '';
    forwardFromGraphIdRef.current = '';
    lastSavedForwardSigRef.current = '';
    setForwardOriginals([]);
    setForwardOriginalsStatus('none');
    setForwardExcluded([]);
    setForwardDroppedImages(0);
    setForwardRestoring(false);
  }, []);

  const loadForwardOriginals = useCallback((emailId: string) => {
    const token = ++forwardTokenRef.current;
    if (!emailId) { setForwardOriginalsStatus('unavailable'); return; }
    setForwardOriginalsStatus('loading');
    fetchGraphAttachmentsList(emailId)
      .then((list) => {
        if (token !== forwardTokenRef.current) return;
        setForwardOriginals(
          list.filter((a) => !a.isInline && a.filename)
            .map((a) => ({
              name: a.filename,
              size: typeof a.size === 'number' ? a.size : null,
              file: !a.odataType || /fileattachment/i.test(String(a.odataType)),
            })),
        );
        setForwardOriginalsStatus('loaded');
      })
      .catch(() => {
        if (token !== forwardTokenRef.current) return;
        setForwardOriginals([]);
        setForwardOriginalsStatus('unavailable'); // never blocks the forward
      });
  }, []);

  /** Every Storage object this composition owns (uploaded here or hydrated from a draft). */
  const collectStoragePaths = useCallback((): string[] => {
    const all = new Set<string>();
    uploadedPathsRef.current.forEach((p) => all.add(p));
    attachmentsRef.current.forEach((a) => { if (a.storagePath) all.add(a.storagePath); });
    // Pictures pasted into the body (also those of a draft opened from storage, which this composer never uploaded).
    inlineImagePaths(htmlBodyRef.current).forEach((p) => all.add(p));
    return Array.from(all);
  }, []);

  /**
   * Drop the current attachments (aborting in-flight uploads) and optionally
   * load `next`. deleteObjects=true ONLY when the content is truly gone (sent /
   * discarded): a scheduled send and a saved draft still reference their objects.
   */
  const resetAttachmentState = useCallback((opts: { deleteObjects: boolean; next?: ComposerAttachment[]; extraPaths?: string[] }) => {
    compositionRef.current += 1;
    uploadAbortsRef.current.forEach((c) => c.abort());
    uploadAbortsRef.current.clear();
    uploadFilesRef.current.clear();
    const paths = [...collectStoragePaths(), ...(opts.extraPaths || [])];
    uploadedPathsRef.current.clear();
    inlineImageSizesRef.current.clear();
    const next = opts.next || [];
    commitAttachments(() => next);
    lastSavedAttachSigRef.current = attachmentStateSignature(toPersistedAttachments(next));
    draftKeyRef.current = newAttachmentId();
    if (opts.deleteObjects && paths.length > 0) void deleteAttachments(paths);
  }, [collectStoragePaths, commitAttachments]);

  // Quoted thread is no longer a separate UI affordance — when replying
  // or forwarding, the quoted block is injected directly into the editor
  // body below a pair of empty paragraphs, and the user can edit/trim/
  // delete it like any other text. No toggle, no extra chrome. This
  // mirrors how Gmail / Outlook Web handle quoted content.

  // ── Client-email autocomplete source ──────────────────────────────────
  const [clientEmailOptions, setClientEmailOptions] = useState<EmailOption[]>([]);
  // lowercased address -> display name, for the recipients a reply prefilled (the original
  // email's own names), so those chips read "Pat Lee <pat@...>" like the classic composer's.
  const [prefillNames, setPrefillNames] = useState<Record<string, string>>({});

  // ── Send + error state ────────────────────────────────────────────────
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The error text of a send the server REFUSED (nothing went out). [retry] shows
  // only while exactly that text is on screen: any later, unrelated error replaces
  // it, and an ambiguous failure (mail may already be delivered) never sets it.
  const [retryableError, setRetryableError] = useState<string | null>(null);
  // ── Undo-send: a 10s grace window before the email actually goes out. ──
  const UNDO_SEND_SECONDS = 10;
  const [pendingSecs, setPendingSecs] = useState<number | null>(null); // null = not pending
  const [pendingWarn, setPendingWarn] = useState<string | null>(null); // e.g. says-attached-but-none
  const pendingPayloadRef = useRef<any>(null);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // When the countdown armed. [send now] replaces [send →] in place, so the
  // second click of a habitual double-click would land on it and skip the
  // undo window entirely — ignore send-now clicks in the first ~400ms.
  const pendingArmedAtRef = useRef<number>(0);
  // ── Attach-invoice picker ──
  const { invoices, customers } = useAppData();
  const [showInvoicePicker, setShowInvoicePicker] = useState(false);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [attachingInvoiceId, setAttachingInvoiceId] = useState<string | null>(null);
  // Synchronous twin of attachingInvoiceId: the double-tap guard and buildPayloadForSend
  // must see "a PDF is being generated" before the state has re-rendered.
  const attachingInvoiceRef = useRef(false);
  const linkedInvoiceIdRef = useRef<string | null>(null); // stamped on send so the invoice is marked SENT
  // ── Reply snippets ──
  const [showSnippets, setShowSnippets] = useState(false);
  const editorRef = useRef<TerminalRichTextHandle>(null);
  // Where focus was when the picker opened (the editor, usually): Esc hands it back.
  const snippetReturnFocusRef = useRef<HTMLElement | null>(null);
  // {{myFirstName}}: the auth profile's name at once, upgraded by users/{uid}.firstName below.
  const [myFirstName, setMyFirstName] = useState<string>(() => firstWord(auth.currentUser?.displayName));
  // {{placeholders}} that are NOT a snippet field ({{name}} in "put {{name}} where their
  // name goes") which the owner chose to keep as written. Send refuses every placeholder
  // otherwise, and a mail about templates could then never be sent. Snippet fields are
  // never keepable: a literal {{firstName}} is always a mistake.
  const [keptVars, setKeptVars] = useState<string[]>([]);
  // How many of each {{token}} the machine-built quote (below the first <hr>) already
  // holds: a client's own mail-merge leftovers are not the owner's gaps. Tokens beyond
  // that count are, wherever they sit (unfilledVariables). Zero whenever there is no record
  // of what the quote held, which fails towards blocking.
  const quoteVarBaselineRef = useRef<VariableCounts>(emptyVariableCounts());
  // ── Tone pass (rewrite the current draft body in a chosen tone) ──
  const [toneBusy, setToneBusy] = useState<string | null>(null);
  // ── Send-later ──
  const [showSendLater, setShowSendLater] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const schedulingRef = useRef(false); // synchronous double-submit guard (state is async)
  // The filename of the attached invoice PDF, so removeAttachment can clear the
  // invoice link when the user pulls the PDF back off (otherwise a later,
  // unrelated send would re-mark that invoice SENT with no PDF actually attached).
  const linkedInvoiceFilenameRef = useRef<string | null>(null);

  // ── Auto-save state ───────────────────────────────────────────────────
  // Visible only when status === 'error' (per the pitch spec). 'saving'
  // + 'saved' are tracked for correctness but not rendered as chrome.
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  // The draft store's own reason when the draft cannot fit one doc (a big pasted
  // image in the body): actionable, unlike the generic "auto-save failed".
  const [autoSaveError, setAutoSaveError] = useState<string | null>(null);
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
  const [lastSavedContent, setLastSavedContent] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const sendingRef = useRef<boolean>(false); // synchronous double-submit guard
  const lastSelfWriteAtRef = useRef<number>(0); // when WE last saved (suppresses our own echo)
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const performAutoSaveRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const lastAutoSaveAtRef = useRef<number>(0);

  // ── Context wiring ────────────────────────────────────────────────────
  const { saveDraft, updateDraft, deleteDraft, currentDraft, draftMode } = useEmailPanel();
  // Compact icon-only button labels on phones — the header runs out of width.
  const isNarrow = useIsNarrow();

  // ── Derived: ranked autocomplete pool for chip inputs ─────────────────
  // Priority group first, then all other client emails. Dedupe by email.
  const rankedSuggestions = useMemo<EmailOption[]>(() => {
    const seen = new Set<string>();
    const out: EmailOption[] = [];
    for (const p of priorityEmails) {
      const k = p.email.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ ...p, group: p.group || 'this job' });
    }
    for (const c of clientEmailOptions) {
      const k = c.email.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ ...c, group: c.group || 'clients' });
    }
    // Last, so a customer record's own label for the same address wins.
    for (const [addr, label] of Object.entries(prefillNames)) {
      if (seen.has(addr)) continue;
      seen.add(addr);
      out.push({ email: addr, label, group: 'this email' });
    }
    return out;
  }, [priorityEmails, clientEmailOptions, prefillNames]);

  // ─────────────────────────────────────────────────────────────────────
  // Effects
  // ─────────────────────────────────────────────────────────────────────

  // Sync ref with latest htmlBody so the unmount-cleanup closure can read it.
  useEffect(() => { htmlBodyRef.current = htmlBody; }, [htmlBody]);

  // ── Live draft updates (mirror of the classic composer) ───────────────
  // Subscribe to the open draft so an edit Andrei makes server-side (rewriting
  // the body, attaching an invoice) appears without a manual reload. Refs keep
  // the onSnapshot closure reading the latest values without re-subscribing.
  const [pendingExternalUpdate, setPendingExternalUpdate] = useState<any | null>(null);
  const lastSavedContentRef = useRef<string>('');
  useEffect(() => { lastSavedContentRef.current = lastSavedContent; }, [lastSavedContent]);

  const applyExternalDraft = useCallback((d: any) => {
    setSubject(d.subject || '');
    setHtmlBody(d.htmlBody || '');
    setTo(Array.isArray(d.to) ? d.to : (d.to ? [d.to] : []));
    setCc(Array.isArray(d.cc) ? d.cc : (d.cc ? [d.cc] : []));
    setBcc(Array.isArray(d.bcc) ? d.bcc : (d.bcc ? [d.bcc] : []));
    const hydrated = hydrateAttachments(d.attachments);
    // Files still uploading (or failed) are not in the doc yet - keep them.
    commitAttachments((prev) => [
      ...hydrated,
      ...prev.filter((a) => a.status === 'uploading' || (a.status === 'error' && !a.trimmed)),
    ]);
    lastSavedAttachSigRef.current = attachmentStateSignature(toPersistedAttachments(hydrated));
    // An invoice attached server-side must mark itself SENT when this composer sends.
    const invLink = invoiceLinkFromDraft(d.linkedInvoiceId, hydrated);
    if (invLink) {
      linkedInvoiceIdRef.current = invLink.id;
      linkedInvoiceFilenameRef.current = invLink.filename;
    }
    if (d.senderAccountId) setSelectedAccountId(d.senderAccountId);
    setLastSavedContent(d.htmlBody || '');
    setHasUnsavedChanges(false);
    setPendingExternalUpdate(null);
  }, [commitAttachments]);

  useEffect(() => {
    if (!currentDraftId) return;
    const unsub = onSnapshot(doc(db, 'email_drafts', currentDraftId), (snap) => {
      if (!snap.exists()) return;
      // Ignore the echo of OUR OWN writes (autosave round-trips through
      // Firestore). hasPendingWrites catches the local optimistic write; the
      // server-ack echo (when the updatedAt serverTimestamp resolves) arrives a
      // beat later with hasPendingWrites=false, so ALSO suppress anything within
      // a short window of our own save. Without this, that echo re-set the
      // editor mid-type and threw the caret to the end. Andrei's edits while the
      // draft sits idle (no recent self-write) still surface.
      if (snap.metadata.hasPendingWrites) return;
      if (Date.now() - lastSelfWriteAtRef.current < 3000) return;
      const d: any = { id: snap.id, ...snap.data() };
      if (d.status && d.status !== 'draft') return;
      const incoming = String(d.htmlBody || '');
      const bodyExternal = incoming !== htmlBodyRef.current && incoming !== lastSavedContentRef.current;
      // Compare against what we would PERSIST: in-flight / failed uploads are
      // local-only and must not read as "Andrei changed the attachments".
      const localPersisted = toPersistedAttachments(attachmentsRef.current);
      const incomingAttachSig = attachmentNameSignature(Array.isArray(d.attachments) ? d.attachments : []);
      const attachExternal = incomingAttachSig !== attachmentNameSignature(localPersisted);
      if (!bodyExternal && !attachExternal) return;
      // A finished upload the 3s autosave hasn't written yet is an unsaved
      // local change too - never overwrite it with the older doc.
      const attachUnsaved = attachmentStateSignature(localPersisted) !== lastSavedAttachSigRef.current;
      const hasUnsaved = htmlBodyRef.current !== lastSavedContentRef.current || attachUnsaved;
      if (hasUnsaved) setPendingExternalUpdate(d);
      else applyExternalDraft(d);
    }, (err) => { console.warn('[TerminalSendEmailForm] draft live-sub error:', err); });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDraftId]);

  // Fetch user's preferred default-reply account (a setting on users/{uid}).
  // Done as a separate one-shot read so the accounts effect can wait on
  // it and pick the right default when starting a reply / forward.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Same order as startUpload: localStorage 'user_uid' is only written at login and
        // is often empty on a persisted session, which left {{myFirstName}} (and the
        // default reply account) unread until the next sign-in.
        const userId = auth.currentUser?.uid || localStorage.getItem('user_uid');
        if (!userId) return;
        const snap = await getDoc(doc(db, 'users', userId));
        if (cancelled) return;
        if (snap.exists()) {
          const data = snap.data() as any;
          setDefaultReplyAccountId(data.defaultReplyAccountId || null);
          const first = firstWord(data.firstName);
          if (first) setMyFirstName(first);
        }
      } catch (err) {
        console.error('[TerminalSendEmailForm] Failed to read default reply account:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch sender accounts. IMPORTANT: getGraphMailAccountsHttp expects a
  // POST with an empty JSON body (callable-style HTTP) — a GET returns
  // nothing useful, which is why the form previously ran with an empty
  // account list and missed the configured signature.
  // Re-runs when the default-reply-account or initialData changes so the
  // selected account can update appropriately for reply / forward modes.
  useEffect(() => {
    let cancelled = false;

    const applyAccounts = (enabled: SenderAccount[], opts: { fromCache: boolean }) => {
      if (cancelled) return;
      setSenderAccounts(enabled);
      if (enabled.length === 0) return;

      // Choose the right default. For reply / forward, prefer the
      // user's default-reply account if they've set one and it's
      // still in the enabled list. Otherwise the first enabled
      // account wins.
      let pickId = enabled[0].id;
      const isReplyOrFwd = !!(initialData?.isReply || initialData?.isReplyAll || initialData?.isForward);
      if (isReplyOrFwd && defaultReplyAccountId) {
        const def = enabled.find(a => a.id === defaultReplyAccountId);
        if (def) pickId = def.id;
      }
      setSelectedAccountId(prev => prev || pickId);
      const acc = enabled.find(a => a.id === pickId);
      setSignatureHtml(acc?.signatureHtml || '');
      // Cache hits don't need a spinner; only show loading when the
      // dropdown was actually empty.
      if (opts.fromCache) setIsFetchingAccounts(false);
    };

    const fetchAccounts = async (): Promise<SenderAccount[]> => {
      if (inflightAccountsPromise) return inflightAccountsPromise;
      inflightAccountsPromise = (async () => {
        try {
          const resp = await authFetch(ACCOUNTS_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({}),
          });
          const data = (await resp.json()) as {
            ok: boolean;
            accounts?: Array<{
              id: string; mailbox: string; displayName: string;
              signatureHtml: string; isEnabled: boolean;
            }>;
          };
          if (!data.ok) {
            console.warn('[TerminalSendEmailForm] getGraphMailAccountsHttp returned ok=false');
            return cachedAccounts || [];
          }
          const enabled: SenderAccount[] = (data.accounts || [])
            .filter(a => a.isEnabled !== false)
            .map(a => ({
              id: a.id,
              mailbox: a.mailbox,
              displayName: a.displayName || a.mailbox,
              signatureHtml: a.signatureHtml || '',
              isEnabled: a.isEnabled !== false,
            }));
          cachedAccounts = enabled;
          cachedAt = Date.now();
          return enabled;
        } finally {
          inflightAccountsPromise = null;
        }
      })();
      return inflightAccountsPromise;
    };

    // Cache-first: if we have a fresh list, populate the dropdown
    // synchronously so the "from" field shows up the moment the composer
    // opens. Otherwise show the spinner while we hit the network.
    const cacheFresh = cachedAccounts && (Date.now() - cachedAt) < ACCOUNTS_TTL_MS;
    if (cacheFresh) {
      applyAccounts(cachedAccounts!, { fromCache: true });
    } else {
      setIsFetchingAccounts(true);
    }

    // Always background-refresh so additions/removals get picked up,
    // even when we served from cache.
    fetchAccounts()
      .then((enabled) => {
        if (cancelled) return;
        // Skip the re-apply if the cached value we already showed is
        // still identical — avoids resetting selectedAccountId.
        if (cacheFresh && cachedAccounts && cachedAccounts.length === enabled.length
            && cachedAccounts.every((a, i) => a.id === enabled[i].id)) {
          return;
        }
        applyAccounts(enabled, { fromCache: false });
      })
      .catch((err) => {
        console.error('[TerminalSendEmailForm] Failed to load accounts:', err);
      })
      .finally(() => {
        if (!cancelled) setIsFetchingAccounts(false);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultReplyAccountId, initialData]);

  // Update signature when account changes.
  useEffect(() => {
    const acc = senderAccounts.find(a => a.id === selectedAccountId);
    setSignatureHtml(acc?.signatureHtml || '');
  }, [selectedAccountId, senderAccounts]);

  // Fetch client email options once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'customers')));
        if (cancelled) return;
        const out: EmailOption[] = [];
        snap.docs.forEach(d => {
          const data = d.data() as any;
          if (typeof data.email === 'string' && data.email.trim()) {
            out.push({
              email: data.email.trim(),
              label: data.name || undefined,
              group: 'clients',
            });
          }
          if (Array.isArray(data.contacts)) {
            data.contacts.forEach((c: any) => {
              if (c?.email && typeof c.email === 'string' && c.email.trim()) {
                out.push({
                  email: c.email.trim(),
                  label: c.name || undefined,
                  group: 'contacts',
                });
              }
            });
          }
        });
        const deduped = Array.from(new Map(out.map(o => [o.email.toLowerCase(), o])).values());
        setClientEmailOptions(deduped);
      } catch (err) {
        console.error('[TerminalSendEmailForm] Failed to load clients:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Process initialData: reply / replyAll / forward / fresh. For reply
  // and forward we build the quoted block and splice it directly into the
  // body ahead of the editor mount, preceded by two empty paragraphs so
  // the user lands the caret in a clean spot above the quote.
  //
  // Guard: the parent (TerminalComposer) memoizes `initialData` against
  // `sourceEmail`, which comes from a live Firestore subscription — every
  // mailbox tick yields a fresh `email` object identity even when
  // contents are identical. Without this signature check the effect
  // re-fires on every tick and blows away whatever the user just typed
  // (symptom: "email content disappears halfway through writing").
  // Re-init only runs when the logical identity of the composition
  // actually changes (different source email, or reply↔forward↔new).
  const initSignatureRef = useRef<string>('');
  // The To / Cc the reply prefill last put in the fields (null when the composition is not
  // a reply or its recipients came from a saved draft). The re-derive effect below compares
  // the live chips against it to know whether the owner has touched them.
  const autoRecipientsRef = useRef<{ to: string[]; cc: string[] } | null>(null);
  // The subject the COMPOSER prefilled (Re:/Fwd:/host-provided) — autosave
  // compares against it to tell "user typed a subject" from "machine put it
  // there", so untouched composers never persist ghost drafts.
  const autoSubjectRef = useRef<string>('');
  useEffect(() => {
    // Opening a saved DRAFT: the draftMode='edit' hydration owns the fields.
    // `initialData` (the panel's stale currentEmailData/{}) arrives a tick after
    // setDraftMode, so without this guard this effect runs after hydration and
    // wipes the draft body — the "click twice to populate" bug. Skip in edit mode.
    if (draftMode === 'edit') return;
    const sig = !initialData
      ? '__null__'
      : [
          initialData.id || '',
          initialData.isReply ? 'r' : '',
          initialData.isReplyAll ? 'a' : '',
          initialData.isForward ? 'f' : '',
          // `to` helps distinguish "new email to X" from "new email to Y"
          Array.isArray(initialData.to) ? initialData.to.join(',') : (initialData.to || ''),
        ].join('|');
    if (sig === initSignatureRef.current) return;
    initSignatureRef.current = sig;
    // A NEW composition never inherits the previous one's saved draft. With currentDraftId kept,
    // the next autosave would write this reply - its prefilled recipients and quote included -
    // over the draft the owner had resumed or was typing (Reply on another email while the
    // panel's composer is still open). The old draft stays as it was last saved.
    if (currentDraftId) { setCurrentDraftId(null); setLastSavedContent(''); }
    // The recipients / chip names below belong to THIS composition, never a previous reply's.
    autoRecipientsRef.current = null;
    setPrefillNames((prev) => (Object.keys(prev).length > 0 ? {} : prev));
    // Fresh composition — clear any invoice link carried from a prior one.
    linkedInvoiceIdRef.current = null;
    linkedInvoiceFilenameRef.current = null;
    // ...and its attachments + forward context. In-flight uploads abort; saved
    // objects are KEPT (a draft of the previous composition may reference them).
    resetAttachmentState({ deleteObjects: false });
    resetForwardState();
    // ...and its quote: the reply / forward branches below record what theirs holds.
    quoteVarBaselineRef.current = emptyVariableCounts();

    if (!initialData) {
      setTo([]); setCc([]); setBcc([]); setSubject('');
      autoSubjectRef.current = '';
      setHtmlBody('');
      return;
    }

    if (initialData.isReply || initialData.isReplyAll) {
      // Who a reply addresses is decided by replyRecipients - one rule set shared by every
      // reply entry point, fed the ORIGINAL email untouched. Mail the owner SENT continues
      // with its recipients (never the owner's own address); mail from a client goes to
      // that client; reply-all never Ccs the signed-in user, the mailbox the mail arrived on
      // or the From account - but keeps a colleague (Lidia) who was on it. "Sent by the
      // owner" is read from the email's folder, type, direction, mailbox and sender, not
      // folder alone: own copies filed in the inbox / deleted / drafts are still the owner's.
      const rr = prefillReplyRecipients(initialData, selectedFromMailbox(), senderAccountsRef.current);
      autoRecipientsRef.current = { to: rr.to, cc: rr.cc };
      setTo(rr.to);
      setCc(rr.cc);
      setPrefillNames(rr.names);
      setBcc([]);
      setSubject(formatReplySubject(initialData.subject));
      autoSubjectRef.current = formatReplySubject(initialData.subject);

      const src = resolveQuotedSource(initialData);
      const original = cleanEmailHtmlForReply(src.html);
      const fromLine = (initialData.from || []).map(f => f.name || f.address).join(', ') || 'Original Sender';
      const dateLine = initialData.date?.seconds
        ? `On ${new Date(initialData.date.seconds * 1000).toLocaleString()}`
        : 'Previously';
      const quoted = `<hr><p><em>${dateLine}, ${fromLine} wrote:</em></p><blockquote>${original}</blockquote>`;
      quoteVarBaselineRef.current = variableCounts(quoted);
      // Two leading paragraphs give the user a clean landing zone; the
      // editor will place the caret at the start when it mounts.
      setHtmlBody(`<p></p><p></p>${quoted}`);
      // Cold cache → we quoted the truncated snippet. Fetch the real body and
      // swap it in, but ONLY if our exact fallback is still present (the user
      // hasn't edited inside the quote). Function replacer: the new HTML may
      // contain `$&`-style sequences that a replacement STRING would expand.
      if (src.usedTextFallback && src.emailId) {
        void getOrFetchGraphEmailBody(src.emailId, 'html').then((b) => {
          if (!b?.content) return;
          const better = cleanEmailHtmlForReply(
            b.contentType === 'html' ? b.content : textToQuotedHtml(b.content),
          );
          if (better && better !== original) {
            setHtmlBody((prev) => {
              if (!prev.includes(original)) return prev;
              // The quote just got longer: what it holds changes with it. Idempotent,
              // because React may run an updater twice.
              quoteVarBaselineRef.current = variableCounts(quoted.replace(original, () => better));
              return prev.replace(original, () => better);
            });
          }
        }).catch(() => { /* keep the snippet */ });
      }
    } else if (initialData.isForward) {
      setTo([]); setCc([]); setBcc([]);
      setSubject(formatForwardSubject(initialData.subject));
      autoSubjectRef.current = formatForwardSubject(initialData.subject);
      const src = resolveQuotedSource(initialData);
      // Keep cid: refs for now so the small ones can be resolved to data: URLs
      // below; the editor body itself is built from the cid-free copy, so a
      // dangling cid: can never reach the editor.
      const originalWithCid = cleanEmailHtmlForReply(src.html, { keepCid: true });
      const original = removeCidImages(originalWithCid);
      const fromLine = (initialData.from || []).map(f => f.name || f.address).join(', ') || 'Original Sender';
      // The panel blanks `to` for a forward (you pick new recipients) but
      // passes the message's real recipients as `originalTo` — that's what
      // the forwarded-message header must show. Header subject shows the
      // ORIGINAL subject, not the Fwd:-prefixed composer field.
      const origToRaw = (initialData as any).originalTo ?? initialData.to;
      const origTo = Array.isArray(origToRaw) ? origToRaw.join(', ') : origToRaw;
      const origSubject = stripSubjectPrefixes(initialData.subject || '');
      const dateLine = initialData.date?.seconds
        ? `Sent: ${new Date(initialData.date.seconds * 1000).toLocaleString()}`
        : '';
      // Leading <hr> matches the reply-branch shape — acts as a stable
      // anchor the signature-insert effect looks for so it can place the
      // signature ABOVE the quoted thread (Gmail/Outlook convention).
      // A function so the async upgrades below can rebuild the WHOLE quoted
      // block (their replace anchor) around a better original - anchoring on
      // `original` alone breaks when it is empty (an image-only email).
      const buildQuoted = (orig: string) => `<hr><p><em>-------- Forwarded Message --------</em><br>
<em>From: ${escapeHtml(fromLine)}</em><br>
<em>Date: ${dateLine}</em><br>
<em>Subject: ${escapeHtml(origSubject)}</em><br>
<em>To: ${escapeHtml(String(origTo || ''))}</em></p>
<br>${orig}`.trim();
      const quoted = buildQuoted(original);
      quoteVarBaselineRef.current = variableCounts(quoted);
      setHtmlBody(`<p></p><p></p>${quoted}`);
      // A forward keeps the original's attachments: remember WHICH message it
      // is (the server copies / re-attaches them) and list them as chips.
      forwardFromEmailIdRef.current = String((initialData as any).emailId || initialData.id || '');
      forwardFromGraphIdRef.current = String(initialData.graph?.id || '');
      loadForwardOriginals(forwardFromEmailIdRef.current);
      const fwdToken = forwardTokenRef.current; // a later composition owns the state after this changes
      // Only attachments within the image budget are worth downloading - a
      // bigger one would be dropped afterwards anyway. (If none qualify the html
      // goes back untouched and the cid: imgs are stripped.)
      // resolveForwardCidImages strips whatever it could not embed without a word;
      // count those so the tray can tell the owner the recipient will not see them
      // (a cross-mailbox forward cannot carry them as attachments either).
      const resolveFwdImages = (raw: string) =>
        resolveForwardCidImages(raw, async (h) => {
          const list = await fetchGraphAttachmentsList(src.emailId);
          const small = list.filter((a) => !(typeof a.size === 'number' && a.size > FORWARD_IMAGE_MAX_BYTES));
          if (small.length === 0) return h;
          return resolveInlineGraphImages(src.emailId, h, { attachments: small });
        }).then((out) => {
          if (fwdToken === forwardTokenRef.current) setForwardDroppedImages(droppedForwardImages(raw, out));
          return out;
        });
      const swapInQuote = (better: string) => {
        if (!better || better === original) return;
        const next = buildQuoted(better);
        setHtmlBody((prev) => {
          if (!prev.includes(quoted)) return prev;
          quoteVarBaselineRef.current = variableCounts(next); // idempotent: React may run an updater twice
          return prev.replace(quoted, () => next);
        });
      };
      // Small cid: images -> data: URLs so the forward renders in the editor
      // (the server's data:->cid conversion re-embeds them on send). Anything
      // unresolvable or over budget is stripped, never left dangling.
      if (originalWithCid !== original) {
        if (src.emailId) {
          void resolveFwdImages(originalWithCid).then(swapInQuote).catch(() => {
            // Images stay stripped - all of them count as dropped.
            if (fwdToken === forwardTokenRef.current) setForwardDroppedImages(countCidImages(originalWithCid));
          });
        } else {
          setForwardDroppedImages(countCidImages(originalWithCid));
        }
      }
      if (src.usedTextFallback && src.emailId) {
        void getOrFetchGraphEmailBody(src.emailId, 'html').then((b) => {
          if (!b?.content) return;
          // The upgraded body goes through the same cid handling.
          return resolveFwdImages(cleanEmailHtmlForReply(
            b.contentType === 'html' ? b.content : textToQuotedHtml(b.content),
            { keepCid: true },
          )).then(swapInQuote);
        }).catch(() => { /* keep the snippet */ });
      }
    } else {
      // Fresh / draft compose (not reply/forward). For new emails the
      // host prefills `to` with the customer's address.
      const toArr = Array.isArray(initialData.to)
        ? initialData.to
        : (initialData.to ? [initialData.to] : []);
      const ccArr = Array.isArray(initialData.cc)
        ? initialData.cc
        : (initialData.cc ? [initialData.cc] : []);
      const bccArr = Array.isArray(initialData.bcc)
        ? initialData.bcc
        : (initialData.bcc ? [initialData.bcc] : []);
      setTo(toArr.map(String).filter(Boolean));
      setCc(ccArr.map(String).filter(Boolean));
      setBcc(bccArr.map(String).filter(Boolean));
      setSubject(initialData.subject || '');
      autoSubjectRef.current = initialData.subject || '';
      setHtmlBody(initialData.html || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  // The reply's recipients depend on facts that can land AFTER the first prefill: the send
  // account list (and so the From account, which counts as "us") still loading, or the same
  // email arriving first without its mailbox / folder / direction and being enriched a tick
  // later. They must never be re-derived by the init effect above (it rebuilds the body and
  // would wipe what is being typed), so this swaps ONLY To / Cc, and only while they still
  // hold exactly what the prefill put there - a recipient the owner added or removed is never
  // overwritten, and nothing else in the composer moves. It also stands still while a send is
  // armed or in flight (the chips are frozen then: what they show must be what goes out) and
  // catches up, if anything changed, the moment the owner undoes it.
  const sendLocked = isSending || pendingSecs !== null || scheduling;
  useEffect(() => {
    const auto = autoRecipientsRef.current;
    if (!auto || draftMode === 'edit' || sendLocked) return;
    if (!initialData || !(initialData.isReply || initialData.isReplyAll)) return;
    const next = prefillReplyRecipients(initialData, selectedFromMailbox(), senderAccounts);
    if (sameAddressList(next.to, auto.to) && sameAddressList(next.cc, auto.cc)) return;
    autoRecipientsRef.current = { to: next.to, cc: next.cc };
    setTo((prev) => (sameAddressList(prev, auto.to) ? next.to : prev));
    setCc((prev) => (sameAddressList(prev, auto.cc) ? next.cc : prev));
    setPrefillNames(next.names);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, senderAccounts, selectedAccountId, draftMode, sendLocked]);

  // Draft resume: populate from currentDraft when draftMode flips to 'edit'.
  // draftMode/currentDraft are GLOBAL (shared by every mounted composer), so
  // guard hard: an inline reply/forward must never be clobbered by a draft
  // resumed in another pane — it would lose the user's in-progress reply and
  // repoint its autosave/delete at the wrong doc.
  //
  // `initialData` is in the deps deliberately: when a draft is opened while a
  // reply composer is up, this effect fires FIRST (context updates land a
  // commit before the panel swaps composerDataForForm), sees the STALE reply
  // initialData, and returns via the guard — it must fire AGAIN when the
  // panel's `{}` initialData arrives a tick later, or the draft opens blank
  // forever ("click twice to populate"). The identity ref below dedups those
  // extra runs so initialData churn can never re-hydrate (and clobber typing)
  // once this draft object is loaded; a deliberate (re)open always hands a
  // fresh draft object and hydrates again.
  const hydratedDraftRef = useRef<unknown>(null);
  useEffect(() => {
    if (draftMode !== 'edit' || !currentDraft) {
      hydratedDraftRef.current = null;
      return;
    }
    // Don't let a draft resumed in another pane clobber an in-progress inline
    // reply/reply-all/forward (they share one global draftMode/currentDraft) - unless it is
    // the very draft THIS composer was opened for: a pane resumed from a saved reply (or
    // promoted from an inline one) carries a reply initialData too, and without this it kept
    // the bare reply template and its prefilled recipients instead of the draft's.
    const ownsDraft = !!resumeDraftId && currentDraft.id === resumeDraftId;
    // A composer opened FOR one draft never takes another (two panes share the one global
    // currentDraft: resuming a second draft must not repoint the first pane at it).
    if (resumeDraftId && !ownsDraft) return;
    if (!ownsDraft && (initialData?.isReply || initialData?.isReplyAll || initialData?.isForward)) return;
    if (hydratedDraftRef.current === currentDraft) return;
    hydratedDraftRef.current = currentDraft;
    autoRecipientsRef.current = null; // the draft's own recipients, not a reply prefill's
    // Outlook text-only drafts sync with htmlBody EMPTY and the content in
    // textBody — render it as simple HTML so the draft doesn't open blank.
    const body = currentDraft.htmlBody
      || (currentDraft.textBody ? textToQuotedHtml(currentDraft.textBody) : '');
    setCurrentDraftId(currentDraft.id ?? null);
    setLastSavedContent(body);
    setSubject(currentDraft.subject || '');
    // A resumed draft keeps no record of what its quote held. Assume nothing: a token
    // in it then reads as the owner's (a block they can clear), never a silent send.
    quoteVarBaselineRef.current = emptyVariableCounts();
    setHtmlBody(body);
    const asList = (v: unknown): string[] =>
      Array.isArray(v) ? (v.filter(Boolean) as string[]) : (v ? [String(v)] : []);
    setTo(asList(currentDraft.to));
    setCc(asList(currentDraft.cc));
    setBcc(asList(currentDraft.bcc));
    // Refs (storagePath) and legacy base64 items both hydrate; an item whose
    // bytes the draft size guard had to drop shows as an error chip instead.
    const hydratedAttachments = hydrateAttachments(currentDraft.attachments);
    resetAttachmentState({ deleteObjects: false, next: hydratedAttachments });
    // Server-made invoice drafts (recurring auto-draft, "email invoice") carry
    // linkedInvoiceId: restore the link, or sending them never marks the invoice
    // SENT and the dashboard "draft ready" flag never clears. It holds only while
    // the PDF chip is there (removeAttachment unlinks when the PDF is pulled off).
    const invLink = invoiceLinkFromDraft(currentDraft.linkedInvoiceId, hydratedAttachments);
    linkedInvoiceIdRef.current = invLink ? invLink.id : null;
    linkedInvoiceFilenameRef.current = invLink ? invLink.filename : null;
    // A resumed FORWARD draft still owes the original's attachments.
    resetForwardState();
    if (currentDraft.isForward && currentDraft.replyToEmailId) {
      forwardFromEmailIdRef.current = String(currentDraft.replyToEmailId);
      loadForwardOriginals(forwardFromEmailIdRef.current);
      // Which originals the owner had left out (and the Graph id) live on the doc,
      // but the panel's draft mapper only carries a fixed field list - read them
      // straight from it. Send waits for this (forwardRestoring): the file they
      // removed must not go out in the gap.
      const fwdToken = forwardTokenRef.current;
      const draftDocId = currentDraft.id;
      if (draftDocId) {
        setForwardRestoring(true);
        getDoc(doc(db, 'email_drafts', draftDocId))
          .then((snap) => {
            if (fwdToken !== forwardTokenRef.current) return;
            const d: any = snap.exists() ? snap.data() : {};
            const names: string[] = Array.isArray(d.forwardExcludeNames)
              ? d.forwardExcludeNames.filter((n: unknown): n is string => typeof n === 'string' && n !== '').slice(0, 200)
              : [];
            if (typeof d.forwardFromGraphId === 'string' && d.forwardFromGraphId) {
              forwardFromGraphIdRef.current = d.forwardFromGraphId;
            }
            // Union, not replace: a chip the owner removed while this was loading
            // must stay removed (never err toward forwarding a file).
            setForwardExcluded((prev) => Array.from(new Set([...names, ...prev])));
            lastSavedForwardSigRef.current = [...names].sort().join('\u0000');
          })
          .catch((err) => {
            console.warn('[TerminalSendEmailForm] could not restore forward exclusions:', err);
            if (fwdToken === forwardTokenRef.current) {
              setError("couldn't restore which original attachments you removed - check the 'from original' chips before sending");
            }
          })
          .finally(() => {
            if (fwdToken === forwardTokenRef.current) setForwardRestoring(false);
          });
      }
    }
    if ((currentDraft as any).senderAccountId) setSelectedAccountId((currentDraft as any).senderAccountId);
    setAutoSaveStatus('idle');
    setHasUnsavedChanges(false);
  }, [draftMode, currentDraft, initialData, resumeDraftId, resetAttachmentState, resetForwardState, loadForwardOriginals]);

  // ─────────────────────────────────────────────────────────────────────
  // Auto-save
  // ─────────────────────────────────────────────────────────────────────

  const getFormSnapshot = useCallback(() => ({
    subject,
    htmlBody,
    to, cc, bcc,
    // Only the persisted shape (contract B): small inline base64 or a Storage
    // ref. Local-only fields and in-flight / failed uploads never reach the doc.
    attachments: toPersistedAttachments(attachments),
    senderAccountId: selectedAccountId,
    // A resumed forward draft has no initialData (the panel passes {}) - keep
    // its forward link so the NEXT resume still knows to keep the originals.
    replyToEmailId: initialData?.id ?? (forwardFromEmailIdRef.current || undefined),
    isReply:   initialData?.isReply   || false,
    isForward: initialData?.isForward || !!forwardFromEmailIdRef.current,
    // Which originals the owner removed (an EMPTY list must be written too, so
    // [restore] sticks) and the Graph id, only while a forward is active. Without
    // them a resumed / expanded forward re-lists every original and would send one
    // the owner had deliberately left out.
    ...(forwardFromEmailIdRef.current
      ? {
          forwardExcludeNames: forwardExcluded.slice(0, 200),
          ...(forwardFromGraphIdRef.current ? { forwardFromGraphId: forwardFromGraphIdRef.current } : {}),
        }
      : {}),
    // The invoice this email marks SENT, so a manually attached one survives a
    // resume too (restored only while its PDF chip is still there).
    ...(linkedInvoiceIdRef.current ? { linkedInvoiceId: linkedInvoiceIdRef.current } : {}),
  }), [subject, htmlBody, to, cc, bcc, attachments, selectedAccountId, initialData, forwardExcluded]);

  const hasContentChanged = (content: string): boolean =>
    content !== lastSavedContent && content.trim().length > 0;

  // Machine-inserted chrome is not user content: the auto-signature and the
  // quoted thread below the first <hr> arrive without a single keystroke.
  // Saving them littered the mailboxes with signature-only ghost drafts —
  // and discarding one soft-deletes it into Deleted Items, where the mail
  // sync used to resurrect it as a scary fake "inbound" email.
  // A {{placeholder}} below the rule beyond what the quote held is the owner's
  // (a snippet dropped under the quote), so it counts as content too.
  const bodyHasUserContent = useCallback((html: string): boolean => {
    const text = ownerRegionHtml(html, signatureHtml)
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (text.length > 0) return true;
    return unfilledVariables(html, signatureHtml, quoteVarBaselineRef.current).length > 0;
  }, [signatureHtml]);

  const performAutoSave = useCallback(async () => {
    if (autoSaveStatus === 'saving') return;
    // A send/schedule in flight owns the content — saving now would re-create
    // the draft doc AFTER the send's cleanup deleted it, leaving a ghost
    // draft of an already-sent email. Skip; on send failure the content stays
    // in the form and the next edit / 30s interval re-arms the save.
    if (sendingRef.current || schedulingRef.current) return;
    // An attachment-only change (upload finished / file removed) must persist
    // too, or a reopened draft would miss it and its Storage object would sit
    // unreferenced until the orphan sweep.
    const attachSig = attachmentStateSignature(toPersistedAttachments(attachments));
    const attachDirty = attachSig !== lastSavedAttachSigRef.current && htmlBody.trim().length > 0;
    // Same for the forward's left-out originals: toggling one changes neither the
    // body nor the attachments, but losing it re-includes a file the owner removed.
    const forwardSig = forwardFromEmailIdRef.current ? forwardExcludeSig : '';
    const forwardDirty = forwardSig !== lastSavedForwardSigRef.current && htmlBody.trim().length > 0;
    if (!hasContentChanged(htmlBody) && !attachDirty && !forwardDirty) { setHasUnsavedChanges(false); return; }
    // Nothing the USER produced yet → nothing worth a draft. (Resumed drafts
    // are exempt: they already exist and edits to them always persist.)
    if (draftMode !== 'edit'
        && attachments.length === 0
        && !bodyHasUserContent(htmlBody)
        && (!subject.trim() || subject.trim() === autoSubjectRef.current.trim())) {
      setHasUnsavedChanges(false);
      return;
    }

    try {
      setAutoSaveStatus('saving');
      setHasUnsavedChanges(true);
      const payload = getFormSnapshot();
      let id: string;
      if (currentDraftId) {
        await updateDraft(currentDraftId, payload);
        id = currentDraftId;
      } else {
        id = await saveDraft(payload);
        // A send may have completed while that write was in flight — the doc
        // we just created is then already orphaned (the send's cleanup ran
        // before this id existed). Drop it instead of adopting it.
        if (sendingRef.current || schedulingRef.current || !htmlBodyRef.current.trim()) {
          try { await deleteDraft(id); } catch { /* best-effort */ }
          setAutoSaveStatus('idle');
          return;
        }
        setCurrentDraftId(id);
      }
      setLastSavedContent(htmlBody);
      lastSavedAttachSigRef.current = attachSig;
      lastSavedForwardSigRef.current = forwardSig;
      lastAutoSaveAtRef.current = Date.now();
      lastSelfWriteAtRef.current = Date.now(); // mark our write so its echo is ignored
      setAutoSaveError(null);
      setAutoSaveStatus('saved');
      setHasUnsavedChanges(false);
      window.setTimeout(() => {
        setAutoSaveStatus(prev => prev === 'saved' ? 'idle' : prev);
      }, 3000);
    } catch (err) {
      console.error('[TerminalSendEmailForm] Auto-save failed:', err);
      const tooLarge = (err as any)?.code === DRAFT_TOO_LARGE_CODE;
      // What makes a draft too big for one Firestore document is almost always pictures still embedded as base64
      // (a forward re-embeds the original's, an older draft has them, a paste whose upload failed keeps one).
      // Move them to Storage - the editor swaps each <img src> in place, caret untouched - and the edit that
      // produces re-arms the debounced autosave, whose draft now fits. Once per failure, never concurrently.
      if (tooLarge && !offloadingImagesRef.current && dataImageCount(htmlBody) > 0) {
        offloadingImagesRef.current = true;
        try {
          const moved = await editorRef.current?.offloadInlineImages();
          if (moved && moved.moved > 0) {
            setAutoSaveError(null);
            setAutoSaveStatus('idle');
            return;
          }
        } catch (offloadErr) {
          console.error('[TerminalSendEmailForm] offloading pasted images failed:', offloadErr);
        } finally {
          offloadingImagesRef.current = false;
        }
      }
      setAutoSaveError(tooLarge ? String((err as any)?.message || '') || null : null);
      setAutoSaveStatus('error');
    }
  }, [autoSaveStatus, htmlBody, lastSavedContent, currentDraftId, getFormSnapshot, saveDraft, updateDraft, deleteDraft, draftMode, attachments, subject, bodyHasUserContent, forwardExcludeSig]);

  useEffect(() => { performAutoSaveRef.current = performAutoSave; }, [performAutoSave]);

  // Persisted-attachments signature: a finished upload / removed file re-arms
  // the debounce below (attachments are not part of the body).
  const persistedAttachSig = useMemo(
    () => attachmentStateSignature(toPersistedAttachments(attachments)),
    [attachments],
  );

  // Debounced save on body changes (3s after last edit)
  useEffect(() => {
    if (draftMode === 'none') return;
    if (!htmlBody) return;
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    autoSaveTimeoutRef.current = setTimeout(() => {
      lastAutoSaveAtRef.current = Date.now();
      performAutoSaveRef.current();
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => { if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current); };
  }, [htmlBody, subject, to, cc, bcc, selectedAccountId, draftMode, persistedAttachSig, forwardExcludeSig]);

  // Interval fallback: every 30s while editing, but skip if a debounce
  // save fired within the last 5s.
  useEffect(() => {
    if (draftMode === 'none') return;
    const id = setInterval(() => {
      if (Date.now() - lastAutoSaveAtRef.current < 5000) return;
      performAutoSaveRef.current();
    }, AUTOSAVE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [draftMode]);

  // Save-on-unmount safety net
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
      if (htmlBodyRef.current?.trim()) {
        try { performAutoSaveRef.current(); } catch { /* best-effort */ }
      }
    };
  }, []);

  // Browser-level unload guard (also while a file is still uploading - leaving
  // would throw that upload away).
  const hasUploadInFlight = attachments.some((a) => a.status === 'uploading') || inlineImageUploads > 0;
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if ((hasUnsavedChanges && htmlBody.trim()) || hasUploadInFlight) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges, htmlBody, hasUploadInFlight]);

  // Closing the composer mid-upload cancels it: nothing would ever reference
  // the half-finished object. The draft autosaves on the way out, so the owner
  // would otherwise believe the file is still on it - say what was dropped.
  useEffect(() => {
    const aborts = uploadAbortsRef.current;
    return () => {
      if (aborts.size > 0) {
        // Deliberately the LATEST attachments: we want what is uploading at unmount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        const names = attachmentsRef.current.filter((a) => a.status === 'uploading').map((a) => a.filename);
        toast.warn(
          names.length > 0
            ? `Upload cancelled - ${names.join(', ')} ${names.length > 1 ? 'were' : 'was'} not attached`
            : 'Upload cancelled - an attachment was not attached',
          { autoClose: false },
        );
      }
      aborts.forEach((c) => c.abort());
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────────
  // Imperative handle
  // ─────────────────────────────────────────────────────────────────────

  const checkForUnsavedChanges = (): boolean => {
    return hasUnsavedChanges && htmlBody.trim().length > 0;
  };

  const triggerManualSave = async () => {
    // Via the ref (always the latest render's closure): the handle below is only
    // re-created when its few deps change, and a stale performAutoSave would
    // persist the attachments as they were before a finished upload - expand-to-
    // pane right after a big file lands would lose it.
    await performAutoSaveRef.current();
  };

  const prepareForDiscard = async () => {
    let draftGone = true;
    if (currentDraftId) {
      try { await deleteDraft(currentDraftId); }
      catch (err) { draftGone = false; console.warn('[TerminalSendEmailForm] Failed to delete draft on discard:', err); }
    }
    // Discarded for good: drop the big attachments' Storage objects too (best
    // effort). If the draft doc could NOT be deleted it still references them.
    resetAttachmentState({ deleteObjects: draftGone });
    resetForwardState();
    setHtmlBody('');
    htmlBodyRef.current = '';
    setHasUnsavedChanges(false);
    setCurrentDraftId(null);
    setLastSavedContent('');
    setAutoSaveStatus('idle');
  };

  useImperativeHandle(ref, () => ({
    checkForUnsavedChanges,
    hasUnsavedChanges,
    currentDraftId,
    hasUploadInFlight,
    triggerSave: triggerManualSave,
    prepareForDiscard,
  }), [hasUnsavedChanges, currentDraftId, htmlBody, hasUploadInFlight]);

  // ─────────────────────────────────────────────────────────────────────
  // Signature helpers
  // ─────────────────────────────────────────────────────────────────────

  const insertSignature = useCallback(() => {
    if (!signatureHtml) return;
    // If body has a signature-like marker already, don't stack duplicates
    if (htmlBody.includes(signatureHtml)) return;
    // Append with spacing before the signature
    const next = (htmlBody.trim() ? htmlBody + '<br><br>' : '') + signatureHtml;
    setHtmlBody(next);
  }, [signatureHtml, htmlBody]);

  // Auto-insert signature once per mount for every compose mode — fresh,
  // reply, reply-all, forward. (Classic only auto-inserted on fresh
  // compose; terminal-mode behaves like Gmail / Outlook / Apple Mail and
  // drops it in by default.)
  //
  // Placement rules:
  //   - fresh compose (empty body): 2 empty paragraphs + signature
  //   - reply / forward (body starts with the quoted-thread `<hr>`
  //     anchor): signature goes BEFORE the `<hr>` so the final order is
  //     reply-text → signature → ── → quoted (the convention in Gmail /
  //     Outlook Web / Apple Mail)
  //   - draft / populated body with no `<hr>` anchor: append at the end
  //
  // Skip when:
  //   - no signature configured for the current sender account
  //   - a draft is being resumed (draftMode='edit' hydrates the saved body;
  //     the signature was already in it at save time)
  //   - body already contains the signature (guard against double-inserts
  //     on re-renders)
  const didAutoInsertSigRef = useRef(false);
  useEffect(() => {
    if (didAutoInsertSigRef.current) return;
    if (draftMode === 'edit') return;
    if (!signatureHtml) return;
    if (htmlBody.includes(signatureHtml)) return;

    const body = htmlBody.trim();

    // Reply / forward path — initialData prepends `<p></p><p></p>` and
    // then the quoted block starting with `<hr>`. Split on that anchor
    // and slot the signature in between, so the user's view reads:
    //   (cursor here) → signature → ── quoted thread ──.
    const hrIdx = htmlBody.indexOf('<hr>');
    if (hrIdx >= 0) {
      const before = htmlBody.slice(0, hrIdx).replace(/\s+$/, '');
      const after  = htmlBody.slice(hrIdx);
      setHtmlBody(`${before}${signatureHtml}<br><br>${after}`);
      didAutoInsertSigRef.current = true;
      return;
    }

    // No quoted anchor: fresh compose OR a plain draft body.
    const leading = '<p></p><p></p>';
    setHtmlBody(body
      ? `${body}<br><br>${signatureHtml}`
      : `${leading}${signatureHtml}`);
    didAutoInsertSigRef.current = true;
  }, [signatureHtml, draftMode, htmlBody]);

  // ─────────────────────────────────────────────────────────────────────
  // Attachments
  // ─────────────────────────────────────────────────────────────────────

  const updateAttachment = useCallback((id: string, patch: Partial<ComposerAttachment>) => {
    commitAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  }, [commitAttachments]);

  // ── Limit accounting ──────────────────────────────────────────────────
  // The server's 20 MiB total covers EVERYTHING it sends: the chips, the kept
  // originals of a forward, and every pasted body image (converted to a cid:
  // attachment). Counting only the chips made the pick-time check and the
  // "x MB of 20 MB" indicator promise room that the server then refused - after
  // the undo countdown, the exact failure this overhaul set out to remove.
  const attachOwnBytes = sumAttachmentBytes(attachments);
  const forwardKeptBytes = keptForwardOriginals.reduce((n, o) => n + (o.size || 0), 0);
  const bodyImageBytes = useMemo(
    () => inlineImageBytes(htmlBody) + storageImageBytes(htmlBody, inlineImageSizesRef.current),
    [htmlBody],
  );
  // Snippet {{placeholders}} still in the owner's OWN words (not the quote's own tokens,
  // not the signature). Send / Later refuse while any remain; the note under the editor
  // says which. Non-snippet names the owner chose to keep as written are let through.
  const openVars = useMemo(
    () => unfilledVariables(htmlBody, signatureHtml, quoteVarBaselineRef.current),
    [htmlBody, signatureHtml],
  );
  const unfilledVars = openVars.filter((v) => isSnippetVariable(v) || keptVars.indexOf(v) < 0);
  // "Keep as written" belongs to the token in THIS message: once it is gone from the
  // text (deleted, or the composer moved on to another mail) the choice goes with it.
  useEffect(() => {
    setKeptVars((prev) => {
      const still = prev.filter((v) => openVars.indexOf(v) >= 0);
      return still.length === prev.length ? prev : still;
    });
  }, [openVars]);
  const attachTotalBytes = attachOwnBytes + forwardKeptBytes + bodyImageBytes;
  // Read by the async pick handler (which must see the latest, not its render's closure).
  const extraLimitBytesRef = useRef(0);
  useEffect(() => { extraLimitBytesRef.current = forwardKeptBytes + bodyImageBytes; }, [forwardKeptBytes, bodyImageBytes]);

  // Why Send / Later must wait (null = go). ONE place, so the buttons, the tray
  // note and buildPayloadForSend can never disagree: uploads running / failed, an
  // invoice PDF still being generated (it only reaches the chips when it arrives,
  // so the attachments list alone would let the email out without it), a resumed
  // forward still reading back the originals the owner removed.
  const sendBlockReason: string | null = forwardRestoring
    ? 'restoring which original attachments you removed - send is available in a moment'
    : attachingInvoiceId !== null
      ? INVOICE_BUSY_REASON
      : inlineImageUploads > 0
        ? IMAGE_UPLOAD_BUSY_REASON
        : attachmentBlockReason(attachments);

  /**
   * Upload one pasted picture (the editor has already compressed it) and hand back the URL for the body. Same
   * folder and rules as file attachments: mail_attachments/{uid}/{draftKey}/..., deleted when the mail is sent,
   * when the draft is discarded, or by the daily orphan sweep. Throws on failure - the editor then falls back to
   * embedding the picture so a paste is never lost.
   */
  const uploadPastedImage = useCallback(async (blob: Blob, name: string): Promise<{ src: string; bytes: number }> => {
    const uid = auth.currentUser?.uid || localStorage.getItem('user_uid') || '';
    if (!uid) throw new Error('not signed in');
    const up = await uploadInlineImage(blob, { uid, draftKey: draftKeyRef.current, filename: name });
    uploadedPathsRef.current.add(up.storagePath);
    inlineImageSizesRef.current.set(up.storagePath, up.bytes);
    return { src: up.url, bytes: up.bytes };
  }, []);

  /**
   * Upload one big file to Cloud Storage with progress. The File is held in
   * uploadFilesRef until it succeeds so a failed chip can [retry] without the
   * user re-picking it. Only the resulting storagePath is ever persisted.
   */
  const startUpload = useCallback((id: string, file: File) => {
    const uid = auth.currentUser?.uid || localStorage.getItem('user_uid') || '';
    if (!uid) {
      uploadFilesRef.current.set(id, file);
      updateAttachment(id, { status: 'error', progress: 0, error: 'not signed in - reload and try again' });
      return;
    }
    const ctrl = new AbortController();
    uploadAbortsRef.current.set(id, ctrl);
    uploadFilesRef.current.set(id, file);
    updateAttachment(id, { status: 'uploading', progress: 0, error: undefined });
    let lastPct = -1;
    uploadAttachment(file, {
      uid,
      draftKey: draftKeyRef.current,
      signal: ctrl.signal,
      // state_changed fires very often on fast links - repaint on whole-percent steps only
      onProgress: (p) => {
        const pct = Math.floor(p);
        if (pct === lastPct) return;
        lastPct = pct;
        updateAttachment(id, { progress: pct });
      },
    })
      .then(({ storagePath }) => {
        uploadAbortsRef.current.delete(id);
        // Removed (or the composer reset) while the last bytes were landing.
        if (!attachmentsRef.current.some((a) => a.id === id)) {
          void deleteAttachments([storagePath]);
          return;
        }
        uploadedPathsRef.current.add(storagePath);
        uploadFilesRef.current.delete(id);
        updateAttachment(id, { status: 'ready', storagePath, progress: 100, error: undefined });
      })
      .catch((err) => {
        uploadAbortsRef.current.delete(id);
        if (err?.name === 'AbortError') return;
        console.warn('[TerminalSendEmailForm] attachment upload failed:', err?.code || err?.message || 'error');
        updateAttachment(id, { status: 'error', error: describeUploadError(err) });
      });
  }, [updateAttachment]);

  /**
   *  Shared per-File processing used by the file picker AND the invoice
   *  attach. Validates at PICK time (exact reason, shared 20 MB numbers), then
   *  keeps small files inline as base64 or uploads big ones to Storage.
   *  Returns the rejection reasons so the caller decides how to surface them.
   *
   *  The chip is committed BEFORE any await (status 'uploading'), so two
   *  overlapping picks each see the other's bytes in attachmentsRef and the
   *  running total can't be overshot by a race.
   */
  const addFilesToAttachments = useCallback(async (files: File[]): Promise<{ added: number; rejected: string[] }> => {
    const rejected: string[] = [];
    let added = 0;
    if (!files.length) return { added, rejected };
    setError(null);
    let inlineRunning = sumInlineBytes(attachmentsRef.current);
    for (const f of files) {
      // Pasted screenshots come through with the browser's generic
      // name "image.png" — tag them with a timestamp so multiple
      // pastes don't collide on filename (and so users can tell
      // them apart in the attachment tray).
      const name = f.name && f.name !== 'image.png'
        ? f.name
        : `pasted-image-${Date.now()}.${(f.type.split('/')[1] || 'png')}`;
      // The forward's kept originals and pasted body images count too (see "Limit accounting").
      const extraBytes = extraLimitBytesRef.current;
      const check = checkCanAdd({
        existingBytes: sumAttachmentBytes(attachmentsRef.current) + extraBytes,
        fileSize: f.size,
        fileName: name,
      });
      if (!check.ok) {
        rejected.push(check.kind === 'total'
          ? `${name}: ${check.reason}${extraBytes > 0 ? ` (the total includes ${formatBytes(extraBytes)} from the original message / pasted images)` : ''}`
          : check.reason);
        continue;
      }
      const id = newAttachmentId();
      const item: ComposerAttachment = {
        id,
        filename: name,
        contentType: f.type || 'application/octet-stream',
        size: f.size,
        status: 'uploading',
        progress: 0,
      };
      commitAttachments((prev) => [...prev, item]);
      added += 1;
      if (classify(f, inlineRunning) === 'inline') {
        inlineRunning += f.size;
        try {
          const b64 = await getFileAsBase64(f);
          updateAttachment(id, { content: b64, status: 'ready', progress: 100 });
        } catch (err) {
          console.error('[TerminalSendEmailForm] attachment read failed:', err);
          commitAttachments((prev) => prev.filter((a) => a.id !== id));
          added -= 1;
          rejected.push(`failed to process ${f.name || 'pasted file'}`);
        }
      } else {
        startUpload(id, f);
      }
    }
    return { added, rejected };
  }, [commitAttachments, updateAttachment, startUpload]);

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const { rejected } = await addFilesToAttachments(Array.from(e.target.files));
    if (rejected.length > 0) setError(rejected.join(' · '));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Note: pasted images used to be routed into attachments here, but
  // users wanted them inline in the editor body (WYSIWYG — what's in
  // the editor is what sends). That logic lives inside TerminalRichText
  // (editorProps.handlePaste / handleDrop): the picture is compressed and
  // uploaded to Cloud Storage via uploadPastedImage and the body holds only
  // an <img src=url>, so it can never push the draft over Firestore's 1 MiB
  // document cap; the server embeds it (cid:) at send. `addFilesToAttachments`
  // above remains the single helper the file-picker and the invoice picker use.

  const retryUpload = (id: string) => {
    const file = uploadFilesRef.current.get(id);
    if (file) startUpload(id, file);
  };

  const removeAttachment = (id: string) => {
    const item = attachmentsRef.current.find((a) => a.id === id);
    if (!item) return;
    const ctrl = uploadAbortsRef.current.get(id);
    if (ctrl) { ctrl.abort(); uploadAbortsRef.current.delete(id); }
    uploadFilesRef.current.delete(id);
    commitAttachments((prev) => prev.filter((a) => a.id !== id));
    // Best-effort: the object is no longer wanted. (The next autosave drops the
    // ref from the draft doc a few seconds later.)
    if (item.storagePath) {
      uploadedPathsRef.current.delete(item.storagePath);
      void deleteAttachments([item.storagePath]);
    }
    // If the user pulls the invoice PDF back off, drop the invoice link too —
    // otherwise the send would still mark that invoice SENT with nothing attached.
    if (linkedInvoiceFilenameRef.current && item.filename === linkedInvoiceFilenameRef.current) {
      linkedInvoiceIdRef.current = null;
      linkedInvoiceFilenameRef.current = null;
    }
  };

  // Forward: the user removed an original attachment (by name - that is what
  // the server matches on). [restore] brings them all back.
  const excludeForwardOriginal = (name: string) => {
    setForwardExcluded((prev) => (prev.includes(name) ? prev : [...prev, name]));
  };

  // ─────────────────────────────────────────────────────────────────────
  // Send
  // ─────────────────────────────────────────────────────────────────────

  const performSend = async (payload: any) => {
    // Synchronous double-submit guard: isSending is async state, so a fast
    // second click / Enter can fire a duplicate send before it paints.
    if (sendingRef.current) return;
    sendingRef.current = true;
    // The send owns the composer content from here — kill any pending
    // debounce autosave so a save can't land mid/after-send and create a
    // draft doc the cleanup below never sees (ghost drafts of sent emails).
    if (autoSaveTimeoutRef.current) { clearTimeout(autoSaveTimeoutRef.current); autoSaveTimeoutRef.current = null; }
    setIsSending(true);
    setError(null);
    setRetryableError(null);
    warmSendSound(); // no-op from the timeout path; covers click paths (send now / retry)
    // How many "from original" chips the owner was looking at (the server's answer
    // is checked against it). Read now: the cleanup below resets the forward state.
    const expectedOriginals = payload?.forwardFromEmailId ? keptForwardOriginals.length : 0;

    // Bookkeeping AFTER a confirmed send. Kept apart from the request so that a
    // failure in here can never surface as "send failed" - the mail is gone, and
    // a [retry] would deliver it twice.
    const afterSent = async () => {
      // Delete the draft on send — both the Firestore doc and the
      // Outlook (Graph) mirror. Marking status:'sent' previously left
      // the Firestore doc behind, and the Graph sync that runs on
      // refresh would re-mirror the still-present Outlook draft as a
      // fresh status:'draft' Firestore doc — making sent emails
      // reappear in the drafts list. There's no UI for sent drafts,
      // so deletion is correct: the sent email lives in Sent Items
      // (where Graph put it on send) and that's the audit trail.
      //
      // 2026-05-13 bug fix: dropped the `draftMode === 'edit'` gate.
      // It only fired for emails the user OPENED from drafts —
      // composing-new emails also have a currentDraftId (set by the
      // auto-save pipeline) but draftMode stays 'new', so those
      // drafts were never deleted on send. They piled up in the
      // panel as ghosts of already-sent emails. The currentDraftId
      // check alone is correct: if we have an id, we have a Firestore
      // doc that needs cleaning up.
      if (currentDraftId) {
        try { await deleteDraft(currentDraftId); }
        catch (err) { console.warn('[TerminalSendEmailForm] Failed to delete draft on send:', err); }
      }
      // Clear form state — same as classic on send success
      setTo([]); setCc([]); setBcc([]);
      setSubject(''); setHtmlBody(''); htmlBodyRef.current = '';
      // The server deletes the objects it sent; drop ours too (best-effort,
      // a missing object is fine) so nothing waits for the daily sweep.
      resetAttachmentState({
        deleteObjects: true,
        extraPaths: (Array.isArray(payload?.attachments) ? payload.attachments : [])
          .map((a: any) => a?.storagePath)
          .filter(Boolean),
      });
      resetForwardState();
      // Drop the invoice link so the NEXT (unrelated) email composed in this
      // still-mounted composer can't re-mark this invoice SENT.
      linkedInvoiceIdRef.current = null;
      linkedInvoiceFilenameRef.current = null;
      setCurrentDraftId(null);
      setLastSavedContent('');
      setAutoSaveStatus('idle');
      setHasUnsavedChanges(false);
      setError(null);
      setRetryableError(null);
    };

    try {
      const bodyJson = JSON.stringify(payload);
      let resp: Response;
      try {
        resp = await authFetch(SEND_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: bodyJson,
        });
      } catch (netErr: any) {
        // fetch() rejects with a TypeError when the connection drops, times out or
        // the phone locks - after the request may already have left. The big-file
        // path runs for minutes and the server has no idempotency key, so the mail
        // may be in Sent Items: say so and offer NO one-click resend. Anything else
        // thrown here is the token step, before any request existed (safe to retry).
        if (netErr?.name === 'TypeError') {
          console.error('[TerminalSendEmailForm] Network error sending (outcome unknown):', netErr);
          setError(SEND_CONNECTION_LOST);
          return;
        }
        throw netErr;
      }
      let data: any = null;
      try { data = await resp.json(); } catch { /* not JSON: a gateway timeout page, a proxy error */ }
      const outcome = classifySendResponse(resp.status, data);
      if (outcome.kind === 'sent') {
        try { await afterSent(); }
        catch (cleanupErr) { console.error('[TerminalSendEmailForm] Cleanup after a successful send failed:', cleanupErr); }
        playSendSound(); // warmed at the send click, so this works from the countdown timeout too
        toast.success('Sent ✓');
        // What the server says did NOT travel (attached emails / links that cannot be
        // copied across mailboxes, a missing original...). Persistent: the composer
        // just cleared, so this is the owner's only chance to re-send the rest.
        sendWarningsFrom(data, expectedOriginals).forEach((w) => toast.warn(w, { autoClose: false }));
        onSendSuccess?.();
      } else if (outcome.kind === 'rejected') {
        // The server said nothing went out: safe to arm Send again.
        setError(outcome.message);
        setRetryableError(outcome.message);
      } else {
        // No trustworthy answer (5xx, gateway page, unreadable body): the mail may
        // be delivered already. No [retry]; only a fresh [send →] (with its undo
        // window) can resend, after the owner has checked Sent Items.
        console.warn('[TerminalSendEmailForm] Send outcome unknown, HTTP', resp.status);
        setError(outcome.message);
      }
    } catch (err: any) {
      // Only reachable when NOTHING was sent: the payload could not be built or
      // the auth token could not be fetched.
      console.error('[TerminalSendEmailForm] Could not start the send:', err);
      const msg = `send failed: ${err?.message || 'could not start the send'}`;
      setError(msg);
      setRetryableError(msg);
    } finally {
      sendingRef.current = false;
      setIsSending(false);
    }
  };

  // The undo-send countdown fires performSend from a setTimeout armed at
  // submit time. That closure is FROZEN at the arming render — but the draft
  // doc is usually created AFTER arming (the first autosave fires 3s after
  // the last keystroke, i.e. after the user clicked send), so the stale
  // closure's currentDraftId was null and the sent email's draft was never
  // deleted. Route the timeout through a ref so the send always runs with
  // current state.
  const performSendRef = useRef(performSend);
  useEffect(() => { performSendRef.current = performSend; });

  // Validate the form and build the send payload. Shared by the immediate
  // send (undo-send countdown) AND send-later. Returns null after setting an
  // error message when the form isn't sendable.
  const buildPayloadForSend = (): { payload: any; warn: string | null } | null => {
    const toEmails  = to.map(emailAddress).filter(Boolean);
    const ccEmails  = cc.map(emailAddress).filter(Boolean);
    const bccEmails = bcc.map(emailAddress).filter(Boolean);

    // Uploads still running / failed, an invoice PDF still being generated:
    // Send and Later are blocked (the tray spells out why). Checked first -
    // waiting is the one thing to do. The invoice check reads the REF, which
    // flips synchronously; state may not have re-rendered yet.
    const attachBlock = attachingInvoiceRef.current ? INVOICE_BUSY_REASON : sendBlockReason;
    if (attachBlock) { setError(`attachments: ${attachBlock}`); return null; }

    // Refuse an over-limit total NOW, with the server's own wording, instead of
    // after the undo countdown (413): same numbers the server will use.
    if (attachTotalBytes > MAIL_ATTACH_MAX_TOTAL_BYTES) {
      const parts = [`${formatBytes(attachOwnBytes)} attached`];
      if (forwardKeptBytes > 0) parts.push(`${formatBytes(forwardKeptBytes)} from the original message`);
      if (bodyImageBytes > 0) parts.push(`${formatBytes(bodyImageBytes)} of pasted images`);
      setError(
        `Attachments total ${formatBytes(attachTotalBytes)}, over the ${formatLimit(MAIL_ATTACH_MAX_TOTAL_BYTES)} limit. `
        + 'Remove or shrink an attachment (or share it as a link) and try again.'
        + (parts.length > 1 ? ` (${parts.join(' + ')})` : ''),
      );
      return null;
    }

    if (!selectedAccountId) { setError("pick a 'from' account"); return null; }
    if (toEmails.length === 0) { setError("'to' is required"); return null; }
    if (!subject.trim()) { setError('subject is required'); return null; }
    if (!htmlBody.trim()) { setError('email body is empty'); return null; }
    // A snippet placeholder nobody filled ({{firstName}}) would reach the client
    // verbatim. Hard stop, same as the validations above, so Send, Send-later,
    // Ctrl+Enter and [retry] (all of which come through here) are covered at once.
    const unfilled = unfilledVariables(htmlBody, signatureHtml, quoteVarBaselineRef.current)
      .filter((v) => isSnippetVariable(v) || keptVars.indexOf(v) < 0);
    if (unfilled.length > 0) {
      const keepable = unfilled.filter((v) => !isSnippetVariable(v));
      setError(describeUnfilledVariables(unfilled)
        + (keepable.length > 0
          ? ` If ${keepable.map(unfilledTokenText).join(', ')} should stay exactly as written, choose [keep as written] under the editor.`
          : ''));
      return null;
    }

    // Reject malformed recipient chips rather than sending them verbatim.
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const badAddr = [...toEmails, ...ccEmails, ...bccEmails].find((a) => !EMAIL_RE.test(String(a).trim()));
    if (badAddr) { setError(`"${badAddr}" is not a valid email address`); return null; }

    // The editor body already contains any quoted content (merged in by
    // the initialData useEffect on reply/forward). WYSIWYG: whatever the
    // user left in the editor is what sends.
    const finalBody = htmlBody;

    const isReplyKind = !!(initialData?.isReply || (initialData as any)?.isReplyAll);
    const sendAttachments = toSendAttachments(attachments);
    const payload = {
      accountId: selectedAccountId,
      to: toEmails.length === 1 ? toEmails[0] : toEmails,
      cc: ccEmails,
      bcc: bccEmails,
      subject,
      html: finalBody,
      text: finalBody.replace(/<[^>]*>?/gm, ''),
      // Contract B: small files carry `content`, big ones only a `storagePath`.
      attachments: sendAttachments.length > 0 ? sendAttachments : undefined,
      // Forward: which message's attachments to keep (the server copies them
      // or re-attaches them) and the ones the user removed. Skipped when the
      // original is KNOWN to have none, so a plain forward stays a plain send.
      ...(forwardFromEmailIdRef.current && !(forwardOriginalsStatus === 'loaded' && forwardOriginals.length === 0)
        ? {
            forwardFromEmailId: forwardFromEmailIdRef.current,
            forwardFromGraphId: forwardFromGraphIdRef.current || undefined,
            forwardExcludeNames: forwardExcluded.length > 0 ? forwardExcluded : undefined,
          }
        : {}),
      // Thread replies in the recipient's client (backend uses createReply when present).
      replyToGraphId: isReplyKind ? ((initialData as any)?.graph?.id || undefined) : undefined,
      replyAll: !!(initialData as any)?.isReplyAll,
      // When an invoice PDF was attached, the backend marks that invoice SENT on
      // real send (APPROVED→SENT), and carry the job link so the reply lands on
      // the job timeline (job workspace queries where linkedJobId == job.id).
      linkedInvoiceId: linkedInvoiceIdRef.current || undefined,
      linkedJobId: (initialData as any)?.linkedJobId || undefined,
      customerId: (initialData as any)?.customerId || undefined,
    };
    // "Says attached but nothing attached" guard — the exact reported bug
    // (email says "invoice attached" with no PDF). Scans only what the OWNER wrote:
    // the quoted thread and the signature contain other people's words ("please
    // find attached the signed quote"), and for Send-later a false hit is a hard
    // block with no way out short of deleting the quote.
    const mentionsAttach = mentionsAttachment(ownerRegionHtml(finalBody, signatureHtml));
    // A forward carries its originals: not "nothing attached" unless we KNOW
    // the list is empty (or every original was removed).
    const originalsMayAttach = !!forwardFromEmailIdRef.current
      && (forwardOriginalsStatus !== 'loaded' || keptForwardOriginals.length > 0);
    const warn = mentionsAttach && (attachments?.length || 0) === 0 && !originalsMayAttach
      ? 'Your message mentions an attachment, but nothing is attached.'
      : null;
    return { payload, warn };
  };

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    // Covers every entry path (button, Ctrl+Enter, native Enter-in-subject
    // form submit): never arm a second send while one is pending/in-flight or
    // a schedule POST is running.
    if (isSending || sendingRef.current || pendingSecs !== null || scheduling || schedulingRef.current) return;
    const built = buildPayloadForSend();
    if (!built) return;
    // Warm the audio context NOW (user gesture) so the sent-sound can play
    // when the countdown fires the send from a timeout later.
    warmSendSound();
    // A previous send's error is stale the moment a new send is armed —
    // leaving it up beside the countdown reads as "this send failed too".
    setError(null);
    setRetryableError(null);
    // Undo-send: hold the payload and count down instead of firing immediately.
    // Catches the whole error class (wrong recipient, missing attachment,
    // half-edited body) with 10s of insurance per send.
    startPendingSend(built.payload, built.warn);
  };

  // Send-later: queue the payload on the server outbox; the 5-min cron fires it
  // through the SAME send path as an immediate send. On success the composer
  // clears exactly like a real send (the queued copy is the source of truth).
  const scheduleSend = async (whenMs: number) => {
    if (scheduling || schedulingRef.current || sendingRef.current || isSending) return;
    // Choosing "later" REPLACES any armed undo-send countdown — otherwise the
    // countdown fires anyway and the email goes out now AND at the scheduled
    // time. Cancelling is the semantics the user obviously intends.
    if (pendingSecs !== null) cancelPendingSend();
    const built = buildPayloadForSend();
    if (!built) return;
    // The says-attached guard is a countdown warning on immediate sends; for a
    // scheduled send there's no countdown to catch it in, so block instead.
    if (built.warn) {
      setError(`${built.warn} Attach the file (or edit the wording) before scheduling.`);
      setShowSendLater(false);
      return;
    }
    schedulingRef.current = true;
    // Same as performSend: no autosave may land once the schedule owns the
    // content — it would re-create the draft doc after the cleanup below.
    if (autoSaveTimeoutRef.current) { clearTimeout(autoSaveTimeoutRef.current); autoSaveTimeoutRef.current = null; }
    setScheduling(true);
    setError(null);
    try {
      const resp = await authFetch(OUTBOX_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'schedule', payload: built.payload, sendAt: new Date(whenMs).toISOString() }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
      // Clear like a successful send — the outbox now owns this email.
      if (currentDraftId) {
        try { await deleteDraft(currentDraftId); }
        catch (err) { console.warn('[TerminalSendEmailForm] Failed to delete draft on schedule:', err); }
      }
      setTo([]); setCc([]); setBcc([]);
      setSubject(''); setHtmlBody(''); htmlBodyRef.current = '';
      // KEEP the Storage objects: the outbox doc references them and the server
      // reads + deletes them when the scheduled mail actually goes out.
      resetAttachmentState({ deleteObjects: false });
      resetForwardState();
      linkedInvoiceIdRef.current = null;
      linkedInvoiceFilenameRef.current = null;
      setCurrentDraftId(null);
      setLastSavedContent('');
      setAutoSaveStatus('idle');
      setHasUnsavedChanges(false);
      setShowSendLater(false);
      toast.success(`scheduled — ${fmtWhen(whenMs)} · cancel anytime in mail → outbox`);
      onSendSuccess?.();
    } catch (e: any) {
      setError(`schedule failed: ${e?.message || 'error'}`);
    } finally {
      schedulingRef.current = false;
      setScheduling(false);
    }
  };

  const clearPendingTimers = () => {
    if (pendingTimeoutRef.current) { clearTimeout(pendingTimeoutRef.current); pendingTimeoutRef.current = null; }
    if (pendingIntervalRef.current) { clearInterval(pendingIntervalRef.current); pendingIntervalRef.current = null; }
  };

  const startPendingSend = (payload: any, warn: string | null = null) => {
    clearPendingTimers();
    setShowSendLater(false); // countdown owns the send now — hide the schedule panel
    pendingArmedAtRef.current = Date.now();
    pendingPayloadRef.current = payload;
    setPendingWarn(warn);
    // Give the user longer to react when we've flagged a likely mistake.
    setPendingSecs(warn ? Math.max(UNDO_SEND_SECONDS, 20) : UNDO_SEND_SECONDS);
    pendingIntervalRef.current = setInterval(() => {
      setPendingSecs((s) => (s === null ? null : Math.max(0, s - 1)));
    }, 1000);
    pendingTimeoutRef.current = setTimeout(() => {
      clearPendingTimers();
      setPendingSecs(null); setPendingWarn(null);
      const p = pendingPayloadRef.current;
      pendingPayloadRef.current = null;
      // Via ref, NOT the closure: the draft doc is often created (first
      // autosave) after this timeout was armed — a stale performSend would
      // miss its currentDraftId and skip the delete-draft-on-send cleanup.
      if (p) void performSendRef.current(p);
    }, (warn ? Math.max(UNDO_SEND_SECONDS, 20) : UNDO_SEND_SECONDS) * 1000);
  };

  const cancelPendingSend = () => {
    clearPendingTimers();
    setPendingSecs(null); setPendingWarn(null);
    pendingPayloadRef.current = null;
    // Disarm [retry] too — after an explicit undo, a stale failure's [retry]
    // must not be offered beside a cancelled send.
    setRetryableError(null);
    // Composer + all state stay intact so the user can keep editing.
  };

  // Fire the pending send NOW (skip the rest of the countdown).
  const sendNow = () => {
    // Swallow the second click of a double-click on [send →] (which this
    // button replaced in place) — that click wasn't a deliberate "skip".
    if (Date.now() - pendingArmedAtRef.current < 400) return;
    clearPendingTimers();
    setPendingSecs(null); setPendingWarn(null);
    const p = pendingPayloadRef.current;
    pendingPayloadRef.current = null;
    if (p) void performSendRef.current(p);
  };

  // Clean up timers if the composer unmounts mid-countdown. Cancel rather than
  // send: firing a delayed request into a torn-down component gives the user no
  // way to see the result or undo, and would contradict the discard-changes
  // path. If they wanted it sent, they'd let the countdown finish.
  useEffect(() => () => { clearPendingTimers(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // [retry] means "arm Send again", NOT "replay the old request". It rebuilds the
  // payload from the composer as it is NOW (an edited body, a removed or added
  // attachment, a fixed recipient all count - a frozen first attempt would send
  // the stale body or point at a Storage object the owner has since removed),
  // re-runs every validation and goes through the undo countdown like any send.
  const handleRetrySend = () => {
    setRetryableError(null);
    void handleSubmit();
  };

  // Tone pass — rewrite ONLY the owner-authored region in the chosen tone (LLM
  // behind an explicit click; never sends). We deliberately DON'T rewrite the
  // appended signature (it carries a logo <img> the model would strip) or the
  // quoted thread (the model would flatten the <blockquote> and could fold the
  // untrusted client text into prose). Split at the first of {signature, <hr>
  // quote anchor}, rewrite the head, re-attach the tail untouched.
  const applyTone = useCallback(async (tone: string) => {
    if (toneBusy) return;
    const hrIdx = htmlBody.indexOf('<hr>');
    const sigIdx = signatureHtml ? htmlBody.indexOf(signatureHtml) : -1;
    const cuts = [hrIdx, sigIdx].filter((i) => i >= 0);
    const cut = cuts.length ? Math.min(...cuts) : -1;
    const ownerRegion = cut >= 0 ? htmlBody.slice(0, cut) : htmlBody;
    const tail = cut >= 0 ? htmlBody.slice(cut) : '';
    if (!ownerRegion.replace(/<[^>]*>/g, '').trim()) {
      setError('write your reply first — nothing to rewrite');
      return;
    }
    setToneBusy(tone);
    setError(null);
    try {
      const resp = await authFetch(REWRITE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ html: ownerRegion, tone }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok || !data.html) throw new Error(data?.error || `HTTP ${resp.status}`);
      setHtmlBody(`${String(data.html)}${tail}`);
      toast.success(`rewrote — ${tone}`);
    } catch (e: any) {
      setError(`tone rewrite failed: ${e?.message || 'error'}`);
    } finally {
      setToneBusy(null);
    }
  }, [toneBusy, htmlBody, signatureHtml]);

  // Invoices the picker offers: recipient's first, then a search over
  // number/customer. Voided/deleted invoices are excluded (you don't email those).
  //
  // NOTE: useAppData().invoices are XERO-SHAPED (PascalCase) — InvoiceID (the
  // Firestore doc id), Status, InvoiceNumber, Contact.{ContactID,Name}, Total,
  // CurrencyCode. There is NO contactEmail on the mapped object, so recipient
  // matching goes recipient address → customers (customer.id === ContactID).
  const recipientAddrs = new Set(to.map((t) => emailAddress(t).toLowerCase()).filter(Boolean));
  const recipientCustomerIds = new Set(
    (customers || [])
      .filter((c: any) => c?.email && recipientAddrs.has(String(c.email).toLowerCase().trim()))
      .map((c: any) => c.id),
  );
  const invoiceOptions = (invoices || [])
    .filter((inv: any) => {
      const st = String((inv as any).Status || '').toUpperCase();
      return st !== 'VOIDED' && st !== 'DELETED';
    })
    .map((inv: any) => ({
      id: String((inv as any).InvoiceID || ''),
      number: String((inv as any).InvoiceNumber || (inv as any).InvoiceID || ''),
      customer: String((inv as any).Contact?.Name || ''),
      total: Number((inv as any).Total || 0),
      currency: String((inv as any).CurrencyCode || ''),
      status: String((inv as any).Status || '').toLowerCase(),
      isRecipient: !!((inv as any).Contact?.ContactID && recipientCustomerIds.has((inv as any).Contact.ContactID)),
    }))
    .filter((x: any) => !!x.id)
    .filter((x: any) => {
      const q = invoiceSearch.trim().toLowerCase();
      if (!q) return true;
      return [x.number, x.customer, String(x.total)]
        .some((f: any) => String(f || '').toLowerCase().includes(q));
    })
    .sort((a: any, b: any) => (b.isRecipient ? 1 : 0) - (a.isRecipient ? 1 : 0))
    .slice(0, 40);

  const attachInvoice = async (inv: { id: string; number: string }) => {
    if (attachingInvoiceRef.current) return;
    attachingInvoiceRef.current = true;
    // Which composition asked. If it is sent / discarded / replaced while the PDF
    // is being generated (seconds on a cold start), the PDF must not land in
    // whatever the composer shows next.
    const token = compositionRef.current;
    setAttachingInvoiceId(inv.id);
    setError(null);
    try {
      const resp = await authFetch('https://us-central1-web-construct-app.cloudfunctions.net/generateInvoicePdfHttp', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceId: inv.id }),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d?.error || `PDF failed (HTTP ${resp.status})`);
      }
      const blob = await resp.blob();
      if (token !== compositionRef.current) return; // that composition is gone
      // Send / Later are blocked while a PDF is generating, so this should never
      // fire - but a PDF that joined a frozen payload would look attached on an
      // email that went out without it, so refuse it loudly rather than add it.
      if (sendingRef.current || schedulingRef.current || pendingPayloadRef.current !== null) {
        throw new Error('the send had already started - the PDF was not attached');
      }
      const filename = `invoice-${inv.number || inv.id}.pdf`;
      // Same pick-time validation + inline/Storage split as any other file
      // (an invoice PDF is ~40 KB so it stays inline), so an oversize one fails
      // here with the exact reason, not after the undo countdown.
      if (!attachmentsRef.current.some((a) => a.filename === filename)) { // else: already attached
        const { rejected } = await addFilesToAttachments([new File([blob], filename, { type: 'application/pdf' })]);
        if (token !== compositionRef.current) return; // reset while the file was being read
        if (rejected.length > 0) throw new Error(rejected[0]);
      }
      linkedInvoiceIdRef.current = String(inv.id);
      linkedInvoiceFilenameRef.current = filename;
      setShowInvoicePicker(false);
      setInvoiceSearch('');
    } catch (e: any) {
      if (token === compositionRef.current) setError(`couldn't attach invoice: ${e?.message || 'error'}`);
    } finally {
      attachingInvoiceRef.current = false;
      setAttachingInvoiceId(null);
    }
  };

  // ─────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────

  const showFromDropdown = senderAccounts.length > 1;
  const currentAccount = senderAccounts.find(a => a.id === selectedAccountId);
  // Freeze the whole composer while a send is in flight OR during the undo-send
  // countdown. Without this, edits made during the countdown (fix a typo, add a
  // recipient) silently diverge from the payload snapshot captured at click time
  // — the on-screen composer and the actually-sent email would disagree. To
  // edit, the user hits [undo] first, which unfreezes everything.
  const composerLocked = isSending || pendingSecs !== null;

  // ── Reply snippets ────────────────────────────────────────────────────
  const openSnippets = () => {
    if (composerLocked) return;
    snippetReturnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setShowSnippets(true);
  };
  const closeSnippets = (reason: SnippetPickerCloseReason) => {
    setShowSnippets(false);
    // Esc / Ctrl+J give focus back, and so do the [x] and a scrim click on desktop: that
    // click left focus on a control which is about to unmount, dropping it to <body> and
    // stranding the caret. Not after an insert (it already focused the editor) and not
    // on a phone, where a scrim tap must not raise the keyboard.
    if (reason === 'inserted' || (reason === 'dismiss' && isNarrow)) return;
    const back = snippetReturnFocusRef.current;
    snippetReturnFocusRef.current = null;
    if (back && back.isConnected) back.focus();
  };
  const toggleSnippets = () => (showSnippets ? closeSnippets('escape') : openSnippets());
  // Into the editor at the caret, through its normal change path (so autosave sees
  // it). On phones the editor is not focused by the insert: that would raise the
  // keyboard over the text that was just added.
  const insertSnippetHtml = (html: string): boolean => {
    const inserted = editorRef.current?.insertHtmlAtCursor(html, { focus: !isNarrow }) ?? false;
    if (inserted) {
      // A gap this insert has just left is new: an earlier "keep as written" for the
      // same name (a typo in the snippet, say) must not cover it.
      const left = findUnresolvedVariables(html);
      if (left.length > 0) setKeptVars((prev) => prev.filter((v) => left.indexOf(v) < 0));
    }
    return inserted;
  };
  // The countdown freezes the composer: a picker left open would insert into a locked editor.
  useEffect(() => { if (composerLocked) setShowSnippets(false); }, [composerLocked]);
  const snippetContext = useMemo(() => (showSnippets
    ? buildSnippetContext({
        to,
        replyFrom: initialData?.from,
        customers,
        customerId: initialData?.customerId,
        // Whose name signs the mail: the selected From mailbox, not just who is signed in.
        myFirstName: senderFirstName(currentAccount, myFirstName),
      })
    : null), [showSnippets, to, initialData, customers, myFirstName, currentAccount]);

  // Send / Later wait for every upload (and need failed ones fixed), a generating
  // invoice PDF and a restoring forward (sendBlockReason): the mail would
  // otherwise go out without the file, or hit the server with a ref to an object
  // that does not exist yet.
  const attachBlockReason = sendBlockReason;
  const attachBlocked = attachBlockReason !== null;
  // What the total beside the chips also contains (kept originals, pasted images).
  const totalIncludes = [
    forwardKeptBytes > 0 ? `${formatBytes(forwardKeptBytes)} from original` : '',
    bodyImageBytes > 0 ? `${formatBytes(bodyImageBytes)} pasted images` : '',
  ].filter(Boolean).join(' + ');
  // Forward pictures that were too big to embed: a same-mailbox forward still
  // carries them (Graph copies every attachment, inline ones too); any other
  // forward cannot, so the owner has to attach them. Unknown mailbox -> assume
  // they are lost (the safer thing to tell someone).
  const originalMailbox = String((initialData as any)?.mailbox || '').toLowerCase();
  const fromMailbox = String(currentAccount?.mailbox || '').toLowerCase();
  const sameMailboxForward = originalMailbox !== '' && originalMailbox === fromMailbox;
  const showDroppedImagesNote = forwardDroppedImages > 0 && !sameMailboxForward;

  // Subject's Enter-to-body hand-off needs a way to reach the Tiptap
  // contenteditable — via the body wrapper, since TerminalRichText doesn't
  // expose an imperative handle.
  const bodyWrapRef = useRef<HTMLDivElement>(null);

  // The action cluster ([sig][📎][🧾][⏰] + send / undo+send-now) renders in
  // the HEADER on desktop and in a sticky BOTTOM bar on phones — the header
  // is the worst one-handed reach zone and scrolls away with the form.
  const composerActions = (
    <>
      {/* Always render the signature button so the affordance is
          discoverable. When no signature is configured for this
          account, click points the user to the settings page where
          they can set one — never a silent failure. */}
      <button
        type="button"
        className={
          'term-form__action'
          + (!signatureHtml ? ' term-form__action--hint' : '')
        }
        onClick={() => {
          if (signatureHtml) {
            insertSignature();
          } else {
            try {
              window.open('/user/account/settings#mail-accounts', '_blank', 'noopener,noreferrer');
            } catch {
              // ignore
            }
          }
        }}
        disabled={composerLocked || isFetchingAccounts}
        title={
          signatureHtml
            ? 'Insert signature at cursor'
            : 'No signature set for this account — click to open Settings → Mail Accounts'
        }
      >
        <span className="term-form__bracket">[</span>
        {signatureHtml ? 'sig' : '+ sig'}
        <span className="term-form__bracket">]</span>
      </button>
      <button
        type="button"
        className="term-form__action term-form__action--snip"
        // Keep the editor's caret (and a phone's keyboard) while the picker opens.
        onMouseDown={(e) => e.preventDefault()}
        onClick={toggleSnippets}
        disabled={composerLocked}
        aria-haspopup="dialog"
        aria-expanded={showSnippets}
        aria-label="Reply snippets"
        title="Insert a reply snippet (Ctrl+J)"
      >
        <span className="term-form__bracket">[</span>📋{isNarrow ? '' : <span className="term-form__action-label"> snippets</span>}<span className="term-form__bracket">]</span>
      </button>
      <button
        type="button"
        className="term-form__action"
        onClick={() => fileInputRef.current?.click()}
        disabled={composerLocked}
        title={`Attach file (max ${formatLimit(MAIL_ATTACH_MAX_FILE_BYTES)} each, ${formatLimit(MAIL_ATTACH_MAX_TOTAL_BYTES)} total)`}
        aria-label="Attach file"
      >
        <span className="term-form__bracket">[</span>📎{isNarrow ? '' : <span className="term-form__action-label"> attach</span>}<span className="term-form__bracket">]</span>
      </button>
      <button
        type="button"
        className="term-form__action"
        onClick={() => setShowInvoicePicker((v) => !v)}
        disabled={composerLocked}
        title="Attach an invoice PDF"
        aria-label="Attach an invoice PDF"
      >
        <span className="term-form__bracket">[</span>🧾{isNarrow ? '' : <span className="term-form__action-label"> invoice</span>}<span className="term-form__bracket">]</span>
      </button>
      <button
        type="button"
        className="term-form__action"
        onClick={() => setShowSendLater((v) => !v)}
        disabled={isSending || isFetchingAccounts || pendingSecs !== null || scheduling || attachBlocked}
        title={attachBlocked ? `Wait - ${attachBlockReason}` : 'Schedule this email to send later'}
        aria-label="Schedule this email to send later"
      >
        <span className="term-form__bracket">[</span>⏰{isNarrow ? '' : <span className="term-form__action-label"> later</span>}<span className="term-form__bracket">]</span>
      </button>
      {pendingSecs !== null ? (
        // Countdown armed: the primary button BECOMES "send now" (skip the
        // wait), with undo right beside it — the two decisions you can make
        // during the grace window, exactly where you just clicked send. One
        // wrapper keeps the pair together when the cluster wraps (.term-form__action-pair).
        <span className="term-form__action-pair">
          <button
            type="button"
            className="term-form__action"
            onClick={cancelPendingSend}
            title="Cancel this send and keep editing"
          >
            <span className="term-form__bracket">[</span>↩{isNarrow ? '' : ' undo'}<span className="term-form__bracket">]</span>
          </button>
          <button
            type="button"
            className="term-form__action term-form__action--send"
            onClick={sendNow}
            title="Skip the countdown and send immediately"
          >
            <span className="term-form__bracket">[</span>{isNarrow ? `→ ${pendingSecs}s` : `send now (${pendingSecs}s)`}<span className="term-form__bracket">]</span>
          </button>
        </span>
      ) : (
        <button
          type="submit"
          className="term-form__action term-form__action--send"
          disabled={isSending || isFetchingAccounts || scheduling || attachBlocked}
          title={attachBlocked ? `Wait - ${attachBlockReason}` : 'Send (Ctrl+Enter)'}
        >
          <span className="term-form__bracket">[</span>
          {isSending ? 'sending…' : 'send →'}
          <span className="term-form__bracket">]</span>
        </button>
      )}
    </>
  );

  return (
    <form
      className="term-form"
      onSubmit={handleSubmit}
      onKeyDown={(e) => {
        // Ctrl/Cmd+J opens (or closes) the reply-snippet picker from anywhere in the composer.
        if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && (e.key || '').toLowerCase() === 'j') {
          e.preventDefault();
          toggleSnippets();
          return;
        }
        // Ctrl/Cmd+Enter sends from anywhere in the composer (incl. the editor).
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
          e.preventDefault();
          if (pendingSecs === null && !isSending && !scheduling) void handleSubmit();
        }
      }}
    >
      {/* Live-update banner — only when Andrei edited this draft AND the user
          has unsaved local edits (clean edits apply silently; never clobber). */}
      {pendingExternalUpdate && (
        <div className="term-form__live-update" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '6px 10px', background: 'rgba(251,191,36,0.12)', borderBottom: '1px solid rgba(251,191,36,0.4)', fontSize: 12, flexWrap: 'wrap' }}>
          <span>Andrei updated this draft — loading replaces your unsaved changes.</span>
          {/* Real padding: this pair decides between discarding YOUR edits and
              discarding Andrei's — it must not be a 12px-tall precision tap. */}
          <span style={{ display: 'flex', gap: 16, whiteSpace: 'nowrap' }}>
            <button type="button" onClick={() => applyExternalDraft(pendingExternalUpdate)} style={{ background: 'none', border: 'none', color: '#fbbf24', cursor: 'pointer', font: 'inherit', padding: '10px 12px' }}>[load update]</button>
            <button type="button" onClick={() => setPendingExternalUpdate(null)} style={{ background: 'none', border: 'none', color: '#6a6a6a', cursor: 'pointer', font: 'inherit', padding: '10px 12px' }}>[keep mine]</button>
          </span>
        </div>
      )}
      {/* ── Header row: from (optional) + actions ─────────────────── */}
      <div className="term-form__head">
        {showFromDropdown ? (
          <span className="term-form__field term-form__field--from">
            <span className="term-form__label">from:</span>
            <select
              className="term-form__from-select"
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              disabled={isFetchingAccounts || composerLocked}
            >
              {senderAccounts.map(a => (
                <option key={a.id} value={a.id}>
                  {a.displayName || a.mailbox}
                </option>
              ))}
            </select>
          </span>
        ) : currentAccount && (
          <span className="term-form__field term-form__field--from">
            <span className="term-form__label">from:</span>
            <span className="term-form__from-static" title={currentAccount.mailbox}>
              {currentAccount.displayName || currentAccount.mailbox}
            </span>
          </span>
        )}

        {/* Hidden file input — must exist regardless of where the action
            buttons render (header on desktop, bottom bar on phones). */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="term-form__file-input"
          onChange={handleFileChange}
          disabled={composerLocked}
        />
        {!isNarrow && (
          <span className={'term-form__head-actions' + (composerLocked ? ' term-form__head-actions--locked' : '')}>{composerActions}</span>
        )}
      </div>

      {/* ── Send-later presets ────────────────────────────────────── */}
      {showSendLater && (
        <div className="term-form__field" style={{ alignItems: 'center', gap: 8, border: '1px solid #2a2a2a', borderRadius: 4, padding: '6px 8px', margin: '4px 0', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, opacity: 0.65 }}>⏰ send:</span>
          {sendLaterPresets().map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => void scheduleSend(p.ms)}
              disabled={scheduling || composerLocked}
              style={{ background: 'none', border: '1px solid var(--bs-border-color, #333)', borderRadius: 4, padding: isNarrow ? '10px 14px' : '2px 10px', cursor: (scheduling || composerLocked) ? 'default' : 'pointer', font: 'inherit', fontSize: isNarrow ? 13 : 12, color: 'inherit', opacity: (scheduling || composerLocked) ? 0.5 : 0.9 }}
            >
              {scheduling ? '…' : p.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setShowSendLater(false)}
            disabled={scheduling}
            style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', fontSize: isNarrow ? 15 : 12, color: 'inherit', opacity: 0.5, marginLeft: 'auto', padding: isNarrow ? '10px 14px' : 0 }}
            aria-label="Close send-later"
          >
            ×
          </button>
        </div>
      )}

      {/* ── Attach-invoice picker ─────────────────────────────────── */}
      {showInvoicePicker && (
        <div className="term-form__field" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4, border: '1px solid #2a2a2a', borderRadius: 4, padding: 8, margin: '4px 0' }}>
          <input
            className="term-form__input"
            placeholder="search invoices by number / customer…"
            value={invoiceSearch}
            onChange={(e) => setInvoiceSearch(e.target.value)}
            // Enter here would implicitly SUBMIT the form — arming a send of
            // the email WITHOUT the invoice the user was searching for.
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) e.preventDefault();
            }}
            disabled={composerLocked}
            // No autofocus on phones: it pops the keyboard + zoom over the
            // list, when the common action is tapping the recipient's
            // invoice already sorted to the top.
            autoFocus={!isNarrow}
          />
          <div style={{ maxHeight: 200, overflowY: 'auto', overscrollBehavior: 'contain' }}>
            {invoiceOptions.length === 0 ? (
              <div style={{ opacity: 0.6, padding: '6px 4px', fontSize: 12 }}>no matching invoices</div>
            ) : invoiceOptions.map((x: any) => (
              <button
                key={x.id}
                type="button"
                onClick={() => attachInvoice(x)}
                disabled={attachingInvoiceId !== null || composerLocked}
                // Phones: two-line rows (number + total, customer beneath) —
                // one cramped line left ~0 width for the customer name, the
                // field that actually distinguishes invoices.
                style={{ display: 'flex', width: '100%', gap: 8, alignItems: 'baseline', flexWrap: isNarrow ? 'wrap' : undefined, rowGap: isNarrow ? 2 : undefined, background: 'none', border: 'none', borderBottom: '1px dashed #222', padding: isNarrow ? '10px 6px' : '5px 4px', cursor: 'pointer', font: 'inherit', textAlign: 'left', color: 'inherit' }}
                title={`Attach ${x.number} as PDF`}
              >
                <span style={{ fontWeight: 600, color: x.isRecipient ? '#7fc98a' : 'inherit' }}>{x.number}</span>
                <span style={{ opacity: 0.75, flex: isNarrow ? '1 1 100%' : 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', order: isNarrow ? 9 : undefined, fontSize: isNarrow ? 12 : undefined }}>{x.customer}</span>
                <span style={{ opacity: 0.75, marginLeft: isNarrow ? 'auto' : undefined }}>{x.currency} {x.total.toFixed(2)}</span>
                <span style={{ opacity: 0.5, fontSize: 11 }}>{x.status}</span>
                <span style={{ opacity: 0.7 }}>{attachingInvoiceId === x.id ? '…' : '+ '}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Reply snippets (panel on desktop, bottom sheet on phones) ── */}
      {showSnippets && snippetContext && (
        <SnippetPicker
          context={snippetContext}
          currentBodyHtml={ownerRegionHtml(htmlBody, signatureHtml)}
          onInsert={insertSnippetHtml}
          onClose={closeSnippets}
          isNarrow={isNarrow}
        />
      )}

      {/* ── To row ────────────────────────────────────────────────── */}
      <div className="term-form__field">
        <span className="term-form__label">to:</span>
        <div className="term-form__field-input">
          <TerminalChipInput
            value={to}
            onChange={setTo}
            suggestions={rankedSuggestions}
            ariaLabel="To"
            autoFocus={to.length === 0 && !initialData?.isReply && !initialData?.isForward}
            disabled={composerLocked}
          />
        </div>
        {!showCcBcc && (
          <button
            type="button"
            className="term-form__toggle"
            onClick={() => setShowCcBcc(true)}
            title="Add CC / BCC"
            disabled={composerLocked}
          >
            <span className="term-form__bracket">[</span>+cc/bcc<span className="term-form__bracket">]</span>
          </button>
        )}
      </div>

      {/* ── Cc / Bcc rows (collapsible) ───────────────────────────── */}
      {showCcBcc && (
        <>
          <div className="term-form__field">
            <span className="term-form__label">cc:</span>
            <div className="term-form__field-input">
              <TerminalChipInput
                value={cc}
                onChange={setCc}
                suggestions={rankedSuggestions}
                ariaLabel="Cc"
                disabled={composerLocked}
              />
            </div>
          </div>
          <div className="term-form__field">
            <span className="term-form__label">bcc:</span>
            <div className="term-form__field-input">
              <TerminalChipInput
                value={bcc}
                onChange={setBcc}
                suggestions={rankedSuggestions}
                ariaLabel="Bcc"
                disabled={composerLocked}
              />
            </div>
            <button
              type="button"
              className="term-form__toggle"
              onClick={() => { setShowCcBcc(false); setCc([]); setBcc([]); }}
              title="Hide CC / BCC"
              disabled={composerLocked}
            >
              <span className="term-form__bracket">[</span>−cc/bcc<span className="term-form__bracket">]</span>
            </button>
          </div>
        </>
      )}

      {/* ── Subject ───────────────────────────────────────────────── */}
      <div className="term-form__field">
        <span className="term-form__label">subject:</span>
        <input
          type="text"
          className="term-form__subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          // Enter used to natively SUBMIT the form — on a phone, "return"
          // after the subject is the natural move-to-body gesture, and it
          // was arming a send of a half-written email. Move focus instead.
          enterKeyHint="next"
          onKeyDown={(e) => {
            // IME guard: the Enter that confirms a Japanese/Chinese/Korean
            // composition must not yank focus mid-composition.
            if ((e.nativeEvent as KeyboardEvent).isComposing) return;
            if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
              e.preventDefault();
              bodyWrapRef.current?.querySelector<HTMLElement>('[contenteditable="true"]')?.focus();
            }
          }}
          placeholder="subject line"
          disabled={composerLocked}
          spellCheck
        />
      </div>

      {/* ── Body editor ───────────────────────────────────────────── */}
      <div className="term-form__body" ref={bodyWrapRef}>
        <TerminalRichText
          ref={editorRef}
          value={htmlBody}
          onChange={setHtmlBody}
          placeholder="write your reply…"
          editable={!composerLocked && !toneBusy}
          minBodyHeight={140}
          uploadImage={uploadPastedImage}
          onImageUploadsChange={setInlineImageUploads}
          onImageRejected={(reason) => setError(reason)}
        />
      </div>

      {/* ── Unfilled snippet placeholders (Send / Later refuse until fixed) ── */}
      {unfilledVars.length > 0 && (
        <div className="term-form__snip-warn" role="status">
          <span aria-hidden="true">⚠</span>{' '}
          fill in before sending: {unfilledVars.map(unfilledTokenText).join(', ')}
          {/* Only for names that are not snippet fields - an explicit, per-name choice
              (type="button": this sits inside the send <form>). */}
          {unfilledVars.filter((v) => !isSnippetVariable(v)).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setKeptVars((prev) => (prev.indexOf(v) < 0 ? [...prev, v] : prev))}
              disabled={composerLocked}
              className="term-form__snip-keep"
              title={`${unfilledTokenText(v)} is not a snippet field - send it exactly as typed`}
            >
              {`[keep ${unfilledTokenText(v)} as written]`}
            </button>
          ))}
        </div>
      )}

      {/* ── Tone pass ─────────────────────────────────────────────── */}
      {htmlBody.replace(/<[^>]*>/g, '').trim().length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 2px 0', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, opacity: 0.6 }}>✨ tone:</span>
          {(['friendlier', 'firmer', 'shorter'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => void applyTone(t)}
              disabled={composerLocked || toneBusy !== null}
              style={{
                background: 'none',
                border: '1px solid var(--bs-border-color, #333)',
                borderRadius: 4,
                padding: isNarrow ? '8px 14px' : '1px 8px',
                cursor: (composerLocked || toneBusy) ? 'default' : 'pointer',
                font: 'inherit',
                fontSize: isNarrow ? 13 : 11,
                color: 'inherit',
                opacity: (composerLocked || toneBusy) ? 0.5 : 0.85,
              }}
              title={`Rewrite the draft to be ${t}`}
            >
              {toneBusy === t ? `${t}…` : t}
            </button>
          ))}
        </div>
      )}

      {/* Quoted thread is now merged into the editor body above — no
          separate toggle. See the initialData useEffect for reply/forward
          body construction. */}

      {/* ── Attachment chips tray ─────────────────────────────────── */}
      {(attachments.length > 0 || keptForwardOriginals.length > 0 || forwardExcluded.length > 0
        || (forwardFromEmailIdRef.current !== '' && forwardOriginalsStatus === 'unavailable')
        || attachBlockReason !== null || showDroppedImagesNote) && (
        <div className="term-form__attach-tray">
          {attachTotalBytes > 0 && (attachments.length > 0 || keptForwardOriginals.length > 0) && (
            <span
              className={
                'term-form__attach-label'
                + (attachTotalBytes > MAIL_ATTACH_MAX_TOTAL_BYTES * 0.9 ? ' term-form__attach-label--warn' : '')
              }
            >
              {attachments.length > 0 && <>{attachments.length} file{attachments.length !== 1 ? 's' : ''} ·{' '}</>}
              {formatTotalIndicator(attachTotalBytes)}
              {totalIncludes && ` (incl. ${totalIncludes})`}
            </span>
          )}
          <div className="term-form__attach-chips">
            {attachments.map(a => (
              <span
                key={a.id}
                className={
                  'term-form__attach-chip'
                  + (a.status === 'uploading' ? ' term-form__attach-chip--uploading' : '')
                  + (a.status === 'error' ? ' term-form__attach-chip--error' : '')
                }
                title={a.status === 'error' && a.error ? `${a.filename} — ${a.error}` : a.filename}
              >
                <span className="term-form__attach-name">{a.filename}</span>
                <span className="term-form__attach-size">
                  {a.status === 'uploading'
                    ? `${formatBytes(a.size)} · ${Math.round(a.progress ?? 0)}%`
                    : a.status === 'error'
                      ? (a.error || 'failed')
                      : formatBytes(a.size)}
                </span>
                {a.status === 'error' && uploadFilesRef.current.has(a.id) && (
                  <button
                    type="button"
                    className="term-form__attach-retry"
                    onClick={() => retryUpload(a.id)}
                    disabled={composerLocked}
                    aria-label={`Retry uploading ${a.filename}`}
                  >retry</button>
                )}
                <button
                  type="button"
                  className="term-form__attach-remove"
                  onClick={() => removeAttachment(a.id)}
                  disabled={composerLocked}
                  aria-label={`Remove ${a.filename}`}
                >×</button>
                {a.status === 'uploading' && (
                  <span className="term-form__attach-bar" style={{ width: `${Math.round(a.progress ?? 0)}%` }} />
                )}
              </span>
            ))}
            {/* Forward: the original's attachments (the server carries them over).
                Read-only apart from the x, which tells it to leave one out. */}
            {keptForwardOriginals.map((o, i) => (
              <span
                key={`orig:${i}:${o.name}`}
                className="term-form__attach-chip term-form__attach-chip--orig"
                title={o.file
                  ? `${o.name} — from the original message`
                  : `${o.name} — an attached email / item from the original message; it may not be included when this is sent from a different mailbox`}
              >
                <span className="term-form__attach-origtag">{o.file ? 'from original' : 'from original · may not be included'}</span>
                <span className="term-form__attach-name">{o.name}</span>
                {o.size !== null && <span className="term-form__attach-size">{formatBytes(o.size)}</span>}
                <button
                  type="button"
                  className="term-form__attach-remove"
                  onClick={() => excludeForwardOriginal(o.name)}
                  disabled={composerLocked}
                  aria-label={`Leave ${o.name} out of the forward`}
                >×</button>
              </span>
            ))}
          </div>
          {forwardExcluded.length > 0 && (
            <span className="term-form__attach-note">
              {forwardExcluded.length} original file{forwardExcluded.length !== 1 ? 's' : ''} left out ·
              {' '}
              <button
                type="button"
                className="term-form__attach-retry"
                onClick={() => setForwardExcluded([])}
                disabled={composerLocked}
              >restore</button>
            </span>
          )}
          {forwardFromEmailIdRef.current !== '' && forwardOriginalsStatus === 'unavailable' && (
            <span className="term-form__attach-note">original attachments will be included if available</span>
          )}
          {showDroppedImagesNote && (
            <span className="term-form__attach-note term-form__attach-note--warn" role="status">
              {forwardDroppedImages} inline picture{forwardDroppedImages !== 1 ? 's' : ''} in the original {forwardDroppedImages !== 1 ? 'were' : 'was'} too large to embed
              and will not reach the recipient - attach {forwardDroppedImages !== 1 ? 'them' : 'it'} separately if needed
            </span>
          )}
          {attachBlockReason && (
            <span className="term-form__attach-note term-form__attach-note--warn" role="status">{attachBlockReason}</span>
          )}
        </div>
      )}

      {/* ── Undo-send countdown bar (desktop) ─────────────────────────
          STICKY while armed: the header [↩ undo]/[send now] pair can be
          scrolled out of view in a long reply (both hosts scroll the
          composer), and Ctrl+Enter arms from anywhere — without this bar
          the countdown would be invisible exactly when the undo insurance
          matters. On phones the bottom action bar carries the countdown. */}
      {pendingSecs !== null && !isNarrow && (
        <div className="term-form__error term-form__pending" role="status" style={{ borderColor: pendingWarn ? '#e06c6c' : '#d8a857' }}>
          <span className="term-form__error-glyph" style={{ color: pendingWarn ? '#e06c6c' : '#d8a857' }}>{pendingWarn ? '⚠' : '↩'}</span>
          <span className="term-form__error-msg">
            {pendingWarn ? `${pendingWarn} Sending in ${pendingSecs}s…` : `sending in ${pendingSecs}s…`}
          </span>
          <span className="term-form__error-actions">
            <button type="button" className="term-form__error-action" onClick={cancelPendingSend}>
              <span className="term-form__bracket">[</span>undo<span className="term-form__bracket">]</span>
            </button>
            <button type="button" className="term-form__error-action" onClick={sendNow}>
              <span className="term-form__bracket">[</span>send now<span className="term-form__bracket">]</span>
            </button>
          </span>
        </div>
      )}

      {/* ── Error + auto-save-failure log lines ───────────────────── */}
      {error && (
        <div className="term-form__error" role="alert">
          <span className="term-form__error-glyph">✗</span>
          <span className="term-form__error-msg">{error}</span>
          <span className="term-form__error-actions">
            {error === retryableError && (
              <button
                type="button"
                className="term-form__error-action"
                onClick={handleRetrySend}
                disabled={composerLocked}
              >
                <span className="term-form__bracket">[</span>retry<span className="term-form__bracket">]</span>
              </button>
            )}
            <button
              type="button"
              className="term-form__error-action"
              onClick={() => setError(null)}
            >
              <span className="term-form__bracket">[</span>dismiss<span className="term-form__bracket">]</span>
            </button>
          </span>
        </div>
      )}

      {/* Auto-save error (rare) — shown only when save failed; idle/saved are silent */}
      {autoSaveStatus === 'error' && (
        <div className="term-form__error term-form__error--autosave" role="status">
          <span className="term-form__error-glyph">!</span>
          <span className="term-form__error-msg">
            {autoSaveError
              ? `auto-save failed — ${autoSaveError}`
              : 'auto-save failed — your draft may not persist on close'}
          </span>
          <span className="term-form__error-actions">
            <button
              type="button"
              className="term-form__error-action"
              onClick={() => performAutoSave()}
            >
              <span className="term-form__bracket">[</span>retry<span className="term-form__bracket">]</span>
            </button>
          </span>
        </div>
      )}

      {/* ── Mobile bottom action bar — LAST child so sticky-bottom truly
          pins to the bottom of the scroll container. One bar owns
          send/attach/later + the countdown on phones: the actions stay
          under the thumb regardless of scroll. While the countdown is
          armed, its status line renders inline here (the action pair
          already swaps to [↩]/[send now]). */}
      {isNarrow && (
        <div className="term-form__bottombar">
          {pendingSecs !== null && (
            <span
              className={`term-form__pendtext${pendingWarn ? ' term-form__pendtext--warn' : ''}`}
              role="status"
            >
              {pendingWarn ? `⚠ ${pendingWarn} ` : '↩ '}sending in {pendingSecs}s…
            </span>
          )}
          {composerActions}
        </div>
      )}
    </form>
  );
});

TerminalSendEmailForm.displayName = 'TerminalSendEmailForm';
export default TerminalSendEmailForm;

